import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiUpload, 
  FiTrash2, 
  FiEdit2, 
  FiX, 
  FiCheck,
  FiMusic,
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiPlay,
  FiPause,
  FiLayers,
  FiPlus,
  FiArrowUp,
  FiArrowDown,
  FiVolume2,
  FiVolumeX,
  FiMaximize,
  FiMinimize,
  FiFolder,
  FiList,
  FiHardDrive,
  FiFileText
} from 'react-icons/fi';
import { materialAPI, labAPI, AudioFile, structuralSegmentsAPI, StructuralSegment, SegmentAudio, adminPodcastsAPI, Podcast, podcastsAPI, adminDialogueCategoriesAPI, DialogueCategory, CategoryDialogue, DialoguePart, DialoguePartAudio, autoplayChimeAPI, testRulesPdfsAPI, TEST_RULES_PDF_SLOTS, TestRulesPdfsMap } from '../services/api';
import { authAPI, UserProfile } from '../services/api';
import AdminSidebar from './AdminSidebar';
import DashboardHeader from './DashboardHeader';
import AudioContentEditor from './AudioContentEditor';
import DialogueBulkContentModal from './DialogueBulkContentModal';
import { useSidebar } from '../contexts/SidebarContext';
import { getAuthenticatedAudioStreamUrl } from '../config/api';
import { STRUCTURAL_SEGMENT_AUDIO_SLOTS } from '../constants/segmentAudioSlots';
import './MaterialManagement.css';
import './Dashboard.css';
import './PodcastPlayer.css';
import MaterialPagination from './MaterialPagination';
import './MaterialPagination.css';
import PodcastVideoThumbnail from './PodcastVideoThumbnail';

/** Empty string → undefined. Invalid input → error message. Whole non‑negative integers only. */
function parseOptionalNonNegativeInt(raw: string): { value: number | undefined; error: string | null } {
  const t = raw.trim();
  if (!t) return { value: undefined, error: null };
  if (!/^\d+$/.test(t)) {
    return {
      value: undefined,
      error: 'Use a whole number (0 or greater), or leave empty.',
    };
  }
  return { value: Number.parseInt(t, 10), error: null };
}

/** Required positive integer (e.g. category id from select). */
function parseRequiredPositiveInt(raw: string, fieldLabel: string): { value: number; error: string | null } {
  const t = raw.trim();
  if (!t) return { value: 0, error: `${fieldLabel} is required.` };
  if (!/^\d+$/.test(t)) {
    return { value: 0, error: `Invalid ${fieldLabel.toLowerCase()}.` };
  }
  const n = Number.parseInt(t, 10);
  if (n < 1) return { value: 0, error: `Invalid ${fieldLabel.toLowerCase()}.` };
  return { value: n, error: null };
}

interface MaterialManagementProps {
  onLogout?: () => void;
  userEmail?: string;
}

const MaterialManagement: React.FC<MaterialManagementProps> = ({ onLogout, userEmail }) => {
  const { isCollapsed } = useSidebar();
  const [materials, setMaterials] = useState<AudioFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState({
    email: userEmail || '',
    organization: 'NAATI',
    first_name: ''
  });
  
  // Upload form state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [segmentNumber, setSegmentNumber] = useState<string>('');
  const [testCategory, setTestCategory] = useState<string>('');
  
  // Edit state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({
    segment_number: '',
    test_category: '',
    is_active: true,
    file_name: ''
  });
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  /** Materials tab: group by structural segment vs flat searchable library */
  const [materialsLibraryMode, setMaterialsLibraryMode] = useState<'by_segment' | 'all_files'>('by_segment');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [filterActive, setFilterActive] = useState<string>('all');
  const [selectedSegment, setSelectedSegment] = useState<number | null>(null);
  
  // Audio preview
  const [playingId, setPlayingId] = useState<number | null>(null);
  const [audioRefs, setAudioRefs] = useState<{ [key: number]: HTMLAudioElement }>({});
  
  // Tab state
  const [activeTab, setActiveTab] = useState<'materials' | 'segments' | 'podcasts' | 'dialogues'>('materials');
  
  // Podcast state
  const [podcasts, setPodcasts] = useState<Podcast[]>([]);
  const [loadingPodcasts, setLoadingPodcasts] = useState(false);
  const [showPodcastModal, setShowPodcastModal] = useState(false);
  const [editingPodcast, setEditingPodcast] = useState<Podcast | null>(null);
  const [uploadingPodcast, setUploadingPodcast] = useState(false);
  const [podcastForm, setPodcastForm] = useState({
    name: '',
    description: '',
    video_file: null as File | null,
    is_free_public: false,
  });
  const [podcastFreeToggleId, setPodcastFreeToggleId] = useState<number | null>(null);

  // Admin podcast player state
  const [selectedPodcast, setSelectedPodcast] = useState<Podcast | null>(null);
  const [isPlayingPodcast, setIsPlayingPodcast] = useState(false);
  const [podcastCurrentTime, setPodcastCurrentTime] = useState(0);
  const [podcastDuration, setPodcastDuration] = useState(0);
  const [podcastVolume, setPodcastVolume] = useState(1);
  const [isPodcastMuted, setIsPodcastMuted] = useState(false);
  const [showPodcastControls, setShowPodcastControls] = useState(true);
  const [podcastBuffering, setPodcastBuffering] = useState(false);
  const [showPodcastVolumeSlider, setShowPodcastVolumeSlider] = useState(false);
  const adminVideoRef = useRef<HTMLVideoElement | null>(null);
  const podcastControlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const podcastVolumeControlRef = useRef<HTMLDivElement>(null);
  const materialAudioRetryRef = useRef<Set<number>>(new Set());

  // Structural Segments state
  const [segments, setSegments] = useState<StructuralSegment[]>([]);
  const [segmentListPage, setSegmentListPage] = useState<StructuralSegment[]>([]);
  const [loadingSegments, setLoadingSegments] = useState(false);
  const [showSegmentModal, setShowSegmentModal] = useState(false);
  const [editingSegment, setEditingSegment] = useState<StructuralSegment | null>(null);
  const [segmentForm, setSegmentForm] = useState({
    name: '',
    description: '',
    segment_number: '',
    test_category: '',
    is_active: true,
    is_free_public: false,
  });
  const [showAddAudioModal, setShowAddAudioModal] = useState(false);
  const [selectedSegmentForAudio, setSelectedSegmentForAudio] = useState<number | null>(null);
  const [segmentLibrarySlotOrder, setSegmentLibrarySlotOrder] = useState<number | null>(null);
  const [segmentSlotUploadingOrder, setSegmentSlotUploadingOrder] = useState<number | null>(null);
  const [selectedAudioFiles, setSelectedAudioFiles] = useState<File[]>([]);
  const [uploadingAudios, setUploadingAudios] = useState(false);
  const [segmentSearchTerm, setSegmentSearchTerm] = useState('');
  const [segmentPage, setSegmentPage] = useState(1);
  const [segmentTotalPages, setSegmentTotalPages] = useState(1);
  const [segmentTotalCount, setSegmentTotalCount] = useState(0);
  const [expandedSegmentId, setExpandedSegmentId] = useState<number | null>(null);
  const [expandedSegmentDetail, setExpandedSegmentDetail] = useState<StructuralSegment | null>(null);
  const [loadingSegmentDetail, setLoadingSegmentDetail] = useState(false);
  const [podcastPage, setPodcastPage] = useState(1);
  const [podcastTotalPages, setPodcastTotalPages] = useState(1);
  const [podcastTotalCount, setPodcastTotalCount] = useState(0);

  // Dialogue Categories state
  const [dialogueCategories, setDialogueCategories] = useState<DialogueCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<DialogueCategory | null>(null);
  const dialogueBriefingFileInputRef = useRef<HTMLInputElement>(null);
  const dialogueBriefingPickRef = useRef<{ dialogueId: number; partId: number } | null>(null);
  const [uploadingDialogueBriefingKey, setUploadingDialogueBriefingKey] = useState<string | null>(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    is_active: true,
  });
  
  // Category dialogues (Dialogue Categories) state
  const [categoryDialogues, setCategoryDialogues] = useState<CategoryDialogue[]>([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [showTopicModal, setShowTopicModal] = useState(false);
  const [editingTopic, setEditingTopic] = useState<CategoryDialogue | null>(null);
  const [selectedCategoryForTopic, setSelectedCategoryForTopic] = useState<number | null>(null);
  const [topicForm, setTopicForm] = useState({
    category: '',
    name: '',
    description: '',
    is_active: true,
    is_free_public: false,
    enable_as_mock: false
  });
  const [showAddAudioToTopicModal, setShowAddAudioToTopicModal] = useState(false);
  const [selectedTopicForAudio, setSelectedTopicForAudio] = useState<number | null>(null);
  const [selectedDialogueForAudio, setSelectedDialogueForAudio] = useState<number | null>(null);
  const [topicSearchTerm, setTopicSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<number | null>(null);
  const [dialoguePage, setDialoguePage] = useState(1);
  const [dialogueTotalPages, setDialogueTotalPages] = useState(1);
  const [dialogueTotalCount, setDialogueTotalCount] = useState(0);
  const [expandedDialogueId, setExpandedDialogueId] = useState<number | null>(null);
  const [expandedDialogueDetail, setExpandedDialogueDetail] = useState<CategoryDialogue | null>(null);
  const [loadingDialogueDetail, setLoadingDialogueDetail] = useState(false);
  const [labItems, setLabItems] = useState<AudioFile[]>([]);
  const [editingContentAudioId, setEditingContentAudioId] = useState<number | null>(null);
  const [bulkContentPart, setBulkContentPart] = useState<{
    topicId: number;
    partId: number;
    partName: string;
    audios: DialoguePartAudio[];
  } | null>(null);

  const resolveContentFileName = useCallback(
    (audioId: number): string => {
      const fromLib = [...materials, ...labItems].find((m) => m.id === audioId);
      if (fromLib) return fromLib.file_name;
      for (const seg of segments) {
        for (const sa of seg.audios || []) {
          if (sa.audio_file_data?.id === audioId) return sa.audio_file_data.file_name;
        }
      }
      for (const cd of categoryDialogues) {
        for (const part of cd.parts || []) {
          if (part.briefing_audio === audioId && part.briefing_audio_detail?.file_name) {
            return part.briefing_audio_detail.file_name;
          }
          for (const al of part.audios || []) {
            if (al.audio_file_data?.id === audioId) return al.audio_file_data.file_name;
          }
        }
      }
      return `Audio #${audioId}`;
    },
    [materials, labItems, segments, categoryDialogues]
  );

  const [autoplayChimeAdmin, setAutoplayChimeAdmin] = useState<{
    autoplay_chime_audio_id: number | null;
    file_name: string | null;
    stream_url?: string | null;
  } | null>(null);
  const [autoplayChimeAdminLoading, setAutoplayChimeAdminLoading] = useState(false);
  const [autoplayChimeSaving, setAutoplayChimeSaving] = useState(false);
  const autoplayChimeFileRef = useRef<HTMLInputElement>(null);
  const autoplayChimePreviewSrc = useMemo(() => {
    const id = autoplayChimeAdmin?.autoplay_chime_audio_id;
    if (id == null) return null;
    return getAuthenticatedAudioStreamUrl(id);
  }, [autoplayChimeAdmin?.autoplay_chime_audio_id]);

  const [testRulesPdfs, setTestRulesPdfs] = useState<TestRulesPdfsMap | null>(null);
  const [testRulesPdfsLoading, setTestRulesPdfsLoading] = useState(false);
  const [testRulesPdfSaving, setTestRulesPdfSaving] = useState<string | null>(null);
  const testRulesPdfInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const fetchTestRulesPdfs = useCallback(async () => {
    setTestRulesPdfsLoading(true);
    try {
      const data = await testRulesPdfsAPI.list();
      setTestRulesPdfs(data);
    } catch {
      setTestRulesPdfs(null);
    } finally {
      setTestRulesPdfsLoading(false);
    }
  }, []);

  const fetchAutoplayChimeAdmin = useCallback(async () => {
    setAutoplayChimeAdminLoading(true);
    try {
      const data = await autoplayChimeAPI.getAdmin();
      setAutoplayChimeAdmin(data);
    } catch {
      setAutoplayChimeAdmin(null);
    } finally {
      setAutoplayChimeAdminLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'materials') {
      fetchMaterials();
      fetchSegments(); // Also fetch segments to get segment names
      void fetchAutoplayChimeAdmin();
      void fetchTestRulesPdfs();
    } else if (activeTab === 'podcasts') {
      fetchPodcasts();
    } else if (activeTab === 'dialogues') {
      fetchMaterials(); // For adding to segments / dialogues
    } else {
      // segments tab - need both for Add Audio modal (Select from Existing Materials)
      fetchSegments();
      fetchMaterials();
    }
    
    // Fetch user profile
    const fetchUserProfile = async () => {
      try {
        const profile = await authAPI.getProfile();
        setUserInfo({
          email: profile.email || userEmail || '',
          organization: profile.organization || 'NAATI',
          first_name: profile.first_name || ''
        });
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };
    fetchUserProfile();
  }, [userEmail, activeTab, fetchAutoplayChimeAdmin, fetchTestRulesPdfs]);

  useEffect(() => {
    if (activeTab === 'segments') {
      fetchSegments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, segmentPage, segmentSearchTerm]);

  useEffect(() => {
    if (activeTab === 'podcasts') {
      fetchPodcasts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, podcastPage]);

  const handleTestRulesPdfSelected = async (
    logicalName: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setError('Please select a PDF file');
      return;
    }
    if (file.size > 30 * 1024 * 1024) {
      setError('PDF must be 30 MB or less');
      return;
    }
    setTestRulesPdfSaving(logicalName);
    setError(null);
    setSuccess(null);
    try {
      await testRulesPdfsAPI.upload(logicalName, file);
      setSuccess(`Test Rules PDF updated (${logicalName}).`);
      await fetchTestRulesPdfs();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to upload PDF');
    } finally {
      setTestRulesPdfSaving(null);
    }
  };

  const handleAutoplayChimeFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('audio/')) {
      setError('Please select an audio file for the chime');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setError('Chime file must be less than 50MB');
      return;
    }
    setAutoplayChimeSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const uploaded = await materialAPI.uploadMaterial({ audio_file: file });
      const updated = await autoplayChimeAPI.updateAdmin(uploaded.id);
      setAutoplayChimeAdmin(updated);
      setSuccess('Autoplay chime uploaded and assigned.');
      await fetchMaterials();
    } catch (err: any) {
      setError(err.message || 'Failed to set autoplay chime');
    } finally {
      setAutoplayChimeSaving(false);
    }
  };

  const handleClearAutoplayChime = async () => {
    if (
      !window.confirm(
        'Remove the autoplay chime? After each examiner clip, recording will start immediately with no sound cue.'
      )
    ) {
      return;
    }
    setAutoplayChimeSaving(true);
    setError(null);
    try {
      const updated = await autoplayChimeAPI.updateAdmin(null);
      setAutoplayChimeAdmin(updated);
      setSuccess('Autoplay chime cleared.');
    } catch (err: any) {
      setError(err.message || 'Failed to clear chime');
    } finally {
      setAutoplayChimeSaving(false);
    }
  };

  const handleAssignMaterialAsAutoplayChime = async (materialId: number) => {
    setAutoplayChimeSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await autoplayChimeAPI.updateAdmin(materialId);
      setAutoplayChimeAdmin(updated);
      setSuccess('This file is now the autoplay chime.');
    } catch (err: any) {
      setError(err.message || 'Failed to assign chime');
    } finally {
      setAutoplayChimeSaving(false);
    }
  };

  const fetchMaterials = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await materialAPI.getMaterials();
      console.log('Fetched materials:', data);
      setMaterials(data);
    } catch (err: any) {
      console.error('Error fetching materials:', err);
      setError(err.message || 'Failed to fetch materials');
    } finally {
      setLoading(false);
    }
  };

  const fetchLabItems = useCallback(async () => {
    try {
      const data = await labAPI.getItems();
      setLabItems(data);
    } catch (err: unknown) {
      console.error('Error fetching lab items:', err);
    }
  }, []);

  useEffect(() => {
    if (showAddAudioModal || showAddAudioToTopicModal) {
      fetchLabItems();
    }
  }, [showAddAudioModal, showAddAudioToTopicModal, fetchLabItems]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('audio/')) {
        setError('Please select an audio file');
        return;
      }
      // Validate file size (max 50MB)
      if (file.size > 50 * 1024 * 1024) {
        setError('File size must be less than 50MB');
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const { value: segNum, error: segErr } = parseOptionalNonNegativeInt(segmentNumber);
      if (segErr) {
        setError(segErr);
        setUploading(false);
        return;
      }
      const newMaterial = await materialAPI.uploadMaterial({
        audio_file: selectedFile,
        segment_number: segNum,
        test_category: testCategory || undefined,
      });

      setMaterials((prev) => [newMaterial, ...prev]);
      setSuccess('Material uploaded successfully!');
      setShowUploadModal(false);
      resetUploadForm();
    } catch (err: any) {
      setError(err.message || 'Failed to upload material');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this material?')) {
      return;
    }

    try {
      await materialAPI.deleteMaterial(id);
      setMaterials((prev) => prev.filter((m) => m.id !== id));
      setSuccess('Material deleted successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to delete material');
    }
  };

  const handleEdit = (material: AudioFile) => {
    setEditingId(material.id);
    setEditForm({
      segment_number: material.segment_number?.toString() || '',
      test_category: material.test_category || '',
      is_active: material.is_active,
      file_name: material.file_name
    });
  };

  const handleUpdate = async (id: number) => {
    try {
      const { value: segNum, error: segErr } = parseOptionalNonNegativeInt(editForm.segment_number);
      if (segErr) {
        setError(segErr);
        return;
      }
      const payload: Parameters<typeof materialAPI.updateMaterial>[1] = {
        test_category: editForm.test_category || undefined,
        is_active: editForm.is_active,
        file_name: editForm.file_name,
      };
      if (editForm.segment_number.trim() === '') {
        payload.segment_number = null;
      } else if (segNum !== undefined) {
        payload.segment_number = segNum;
      }

      const updated = await materialAPI.updateMaterial(id, payload);

      setMaterials((prev) => prev.map((m) => (m.id === id ? updated : m)));
      setEditingId(null);
      setSuccess('Material updated successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to update material');
    }
  };

  const resetUploadForm = () => {
    setSelectedFile(null);
    setSegmentNumber('');
    setTestCategory('');
    const fileInput = document.getElementById('audio-file-input') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDuration = (seconds: number | null): string => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const attachMaterialAudioErrorHandler = useCallback((audio: HTMLAudioElement, materialId: number) => {
    const mark = '__naatiMaterialAudioErr';
    if ((audio as unknown as Record<string, boolean>)[mark]) return;
    (audio as unknown as Record<string, boolean>)[mark] = true;
    audio.addEventListener('error', async () => {
      if (materialAudioRetryRef.current.has(materialId)) {
        setError('Could not play audio. The link may have expired — try play again.');
        setPlayingId(null);
        return;
      }
      materialAudioRetryRef.current.add(materialId);
      try {
        const list = await materialAPI.getMaterials();
        setMaterials(list);
        const fresh = list.find((m) => m.id === materialId);
        if (!fresh?.stream_url) {
          setError('Could not refresh audio URL.');
          setPlayingId(null);
          return;
        }
        audio.src = fresh.stream_url;
        audio.load();
        await audio.play();
      } catch {
        setError('Could not refresh audio.');
        setPlayingId(null);
      } finally {
        materialAudioRetryRef.current.delete(materialId);
      }
    });
  }, []);

  const handlePlayPause = (material: AudioFile) => {
    const latestMaterial = materials.find((m) => m.id === material.id) ?? material;
    materialAudioRetryRef.current.delete(material.id);

    if (playingId === material.id) {
      if (audioRefs[material.id]) {
        audioRefs[material.id].pause();
      }
      setPlayingId(null);
      return;
    }

    if (playingId !== null && audioRefs[playingId]) {
      audioRefs[playingId].pause();
      audioRefs[playingId].currentTime = 0;
    }

    const playUrl = getAuthenticatedAudioStreamUrl(latestMaterial.id);
    if (!playUrl) {
      setError('Not signed in — cannot play audio. Log in again.');
      return;
    }

    if (!audioRefs[material.id]) {
      const audio = new Audio(playUrl);
      audio.addEventListener('ended', () => setPlayingId(null));
      attachMaterialAudioErrorHandler(audio, material.id);
      setAudioRefs((prev) => ({ ...prev, [material.id]: audio }));
      audio.play().catch(() => {
        setError('Failed to start playback');
        setPlayingId(null);
      });
    } else {
      const audio = audioRefs[material.id];
      attachMaterialAudioErrorHandler(audio, material.id);
      audio.src = playUrl;
      audio.load();
      audio.play().catch(() => {
        setError('Failed to start playback');
        setPlayingId(null);
      });
    }
    setPlayingId(material.id);
  };

  // Group materials by structural segment ID (not segment_number)
  // First, create a map of audio_file_id -> structural_segment_id from segments
  const audioToSegmentMap = new Map<number, number>();
  segments.forEach((segment: StructuralSegment) => {
    if (segment.audios && segment.audios.length > 0) {
      segment.audios.forEach((segmentAudio: any) => {
        if (segmentAudio.audio_file_data && segmentAudio.audio_file_data.id) {
          audioToSegmentMap.set(segmentAudio.audio_file_data.id, segment.id);
        }
      });
    }
  });

  // Group materials by structural segment ID
  const materialsBySegmentId = new Map<number, AudioFile[]>();
  const unassignedMaterials: AudioFile[] = [];

  materials.forEach((material) => {
    const segmentId = audioToSegmentMap.get(material.id);
    if (segmentId !== undefined) {
      if (!materialsBySegmentId.has(segmentId)) {
        materialsBySegmentId.set(segmentId, []);
      }
      materialsBySegmentId.get(segmentId)!.push(material);
    } else {
      // Material not assigned to any structural segment
      unassignedMaterials.push(material);
    }
  });

  // Convert to array format for compatibility with existing code
  // Use segment ID as key, but we'll need to map it back to segment_number for display
  const materialsBySegment: Record<number, AudioFile[]> = {};
  
  // Add materials grouped by structural segment ID
  segments.forEach((segment: StructuralSegment) => {
    const segmentMaterials = materialsBySegmentId.get(segment.id) || [];
    if (segmentMaterials.length > 0) {
      // Use segment ID as key (we'll handle mapping in render)
      materialsBySegment[segment.id] = segmentMaterials;
    }
  });

  // Add unassigned materials as segment 0 (for backward compatibility)
  if (unassignedMaterials.length > 0) {
    materialsBySegment[0] = unassignedMaterials;
  }

  // Get segment IDs and sort them
  // For structural segments, use their ID; for unassigned, use 0
  const segmentIds = [
    ...segments.map((s: StructuralSegment) => s.id),
    ...(unassignedMaterials.length > 0 ? [0] : [])
  ].sort((a, b) => {
    // Sort: unassigned (0) last, then by their segment_number or ID
    if (a === 0) return 1;
    if (b === 0) return -1;
    const segA = segments.find((s: StructuralSegment) => s.id === a);
    const segB = segments.find((s: StructuralSegment) => s.id === b);
    const numA = segA?.segment_number ?? segA?.id ?? 0;
    const numB = segB?.segment_number ?? segB?.id ?? 0;
    return numA - numB;
  });
  
  // Filter segments by search term (using segment IDs)
  const filteredSegmentIds = segmentIds.filter(segmentId => {
    if (!searchTerm.trim()) return true;
    
    const segmentMaterials = materialsBySegment[segmentId];
    if (!segmentMaterials || segmentMaterials.length === 0) return false;
    
    const searchLower = searchTerm.toLowerCase().trim();
    
    // Find matching structural segment by ID
    let matchingSegment: StructuralSegment | undefined;
    if (segmentId === 0) {
      matchingSegment = undefined;
    } else {
      matchingSegment = segments.find((s: StructuralSegment) => s.id === segmentId && s.is_active);
    }
    
    // Get segment name for search
    let segmentName = `Segment ${segmentId}`;
    if (matchingSegment) {
      segmentName = matchingSegment.name;
    } else if (segmentId === 0) {
      const category = segmentMaterials[0]?.test_category;
      if (category) {
        segmentName = category;
      } else {
        const firstFileName = segmentMaterials[0]?.file_name;
        if (firstFileName) {
          const nameWithoutExt = firstFileName.replace(/\.[^/.]+$/, '');
          const parts = nameWithoutExt.split(/[-_]/);
          if (parts.length > 0 && parts[0].trim().length > 0) {
            segmentName = parts[0].trim();
          } else if (nameWithoutExt.trim().length > 0) {
            segmentName = nameWithoutExt.trim();
          }
        }
      }
    } else if (segmentMaterials[0]?.test_category) {
      segmentName = segmentMaterials[0].test_category;
    }
    
    // Search in segment name
    if (segmentName.toLowerCase().includes(searchLower)) {
      return true;
    }
    
    // Search in segment number
    if (matchingSegment && matchingSegment.segment_number !== null) {
      if (`Segment ${matchingSegment.segment_number}`.toLowerCase().includes(searchLower)) {
        return true;
      }
    }
    
    // Search in file names
    if (segmentMaterials.some(m => 
      m.file_name.toLowerCase().includes(searchLower)
    )) {
      return true;
    }
    
    // Search in test category
    if (segmentMaterials.some(m => 
      m.test_category && m.test_category.toLowerCase().includes(searchLower)
    )) {
      return true;
    }
    
    // Search in structural segment description
    if (matchingSegment && matchingSegment.description) {
      if (matchingSegment.description.toLowerCase().includes(searchLower)) {
        return true;
      }
    }
    
    return false;
  });

  // Get materials for selected segment and sort by file name (ascending)
  const selectedSegmentMaterials = selectedSegment !== null 
    ? (materialsBySegment[selectedSegment] || []).sort((a, b) => {
        // Sort by file name (ascending - from 000 to 016)
        return a.file_name.localeCompare(b.file_name, undefined, { 
          numeric: true, 
          sensitivity: 'base' 
        });
      })
    : [];

  const categories = Array.from(new Set(materials.map(m => m.test_category).filter((cat): cat is string => Boolean(cat))));

  const materialIdToSegmentName = useMemo(() => {
    const m = new Map<number, string>();
    segments.forEach((seg: StructuralSegment) => {
      (seg.audios || []).forEach((sa: SegmentAudio) => {
        const id = sa.audio_file_data?.id;
        if (id != null) m.set(id, seg.name);
      });
    });
    return m;
  }, [segments]);

  const materialsLibraryStats = useMemo(() => {
    let assigned = 0;
    let bytes = 0;
    materials.forEach((x) => {
      bytes += x.file_size || 0;
      if (materialIdToSegmentName.has(x.id)) assigned += 1;
    });
    const total = materials.length;
    const unassigned = total - assigned;
    const active = materials.filter((x) => x.is_active).length;
    let segmentBuckets = 0;
    segments.forEach((seg: StructuralSegment) => {
      const has = (seg.audios || []).some((sa: SegmentAudio) =>
        materials.some((m) => m.id === sa.audio_file_data?.id)
      );
      if (has) segmentBuckets += 1;
    });
    if (materials.some((m) => !materialIdToSegmentName.has(m.id))) segmentBuckets += 1;
    return { total, assigned, unassigned, active, bytes, segmentBuckets };
  }, [materials, materialIdToSegmentName, segments]);

  const libraryDialogueTotal = useMemo(
    () => dialogueCategories.reduce((sum, category) => sum + (category.dialogues_count ?? 0), 0),
    [dialogueCategories],
  );

  const allMaterialsFiltered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return [...materials]
      .filter((m) => {
        if (!q) return true;
        if (m.file_name.toLowerCase().includes(q)) return true;
        if (m.test_category?.toLowerCase().includes(q)) return true;
        const seg = materialIdToSegmentName.get(m.id);
        if (seg?.toLowerCase().includes(q)) return true;
        if (!materialIdToSegmentName.has(m.id) && (q.includes('unassign') || q.includes('library'))) return true;
        return false;
      })
      .sort((a, b) =>
        a.file_name.localeCompare(b.file_name, undefined, { numeric: true, sensitivity: 'base' })
      );
  }, [materials, searchTerm, materialIdToSegmentName]);

  // Structural Segments functions
  const fetchSegments = async () => {
    setLoadingSegments(true);
    setError(null);
    try {
      const lookupRes = await structuralSegmentsAPI.getSegments({ page: 1, page_size: 500 });

      if (activeTab === 'materials' || activeTab === 'dialogues') {
        const fullSegments = await Promise.all(
          lookupRes.results.map((s) => structuralSegmentsAPI.getSegment(s.id))
        );
        setSegments(fullSegments);
      } else {
        setSegments(lookupRes.results);
      }

      if (activeTab === 'segments') {
        const listRes = await structuralSegmentsAPI.getSegments({
          page: segmentPage,
          page_size: 20,
          q: segmentSearchTerm.trim() || undefined,
        });
        setSegmentListPage(listRes.results);
        setSegmentTotalPages(listRes.total_pages);
        setSegmentTotalCount(listRes.count);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch segments');
    } finally {
      setLoadingSegments(false);
    }
  };

  const fetchPodcasts = async () => {
    setLoadingPodcasts(true);
    setError(null);
    try {
      const res = await adminPodcastsAPI.listPodcasts({
        page: podcastPage,
        page_size: 24,
      });
      setPodcasts(res.results);
      setPodcastTotalPages(res.total_pages);
      setPodcastTotalCount(res.count);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch podcasts');
    } finally {
      setLoadingPodcasts(false);
    }
  };

  const toggleManageSegment = async (id: number) => {
    if (expandedSegmentId === id) {
      setExpandedSegmentId(null);
      setExpandedSegmentDetail(null);
      return;
    }
    setExpandedSegmentId(id);
    setLoadingSegmentDetail(true);
    try {
      const detail = await structuralSegmentsAPI.getSegment(id);
      setExpandedSegmentDetail(detail);
    } catch (err: any) {
      setError(err.message || 'Failed to load segment details');
      setExpandedSegmentId(null);
      setExpandedSegmentDetail(null);
    } finally {
      setLoadingSegmentDetail(false);
    }
  };

  const refreshSegmentViews = async () => {
    await fetchSegments();
    if (expandedSegmentId != null) {
      try {
        const detail = await structuralSegmentsAPI.getSegment(expandedSegmentId);
        setExpandedSegmentDetail(detail);
      } catch {
        setExpandedSegmentId(null);
        setExpandedSegmentDetail(null);
      }
    }
  };

  /** List API omits `audios`; use expanded detail when managing segment slots. */
  const getSegmentAudioAtOrder = useCallback(
    (segment: StructuralSegment, order: number): SegmentAudio | undefined =>
      (segment.audios || []).find((sa: SegmentAudio) => sa.order === order),
    []
  );

  const handleCreatePodcast = async () => {
    if (!podcastForm.name.trim()) {
      setError('Podcast name is required');
      return;
    }
    if (!editingPodcast && !podcastForm.video_file) {
      setError('Video file is required');
      return;
    }

    setUploadingPodcast(true);
    setError(null);
    try {
      if (editingPodcast) {
        const formData = new FormData();
        formData.append('name', podcastForm.name);
        formData.append('description', podcastForm.description || '');
        formData.append('is_free_public', podcastForm.is_free_public ? 'true' : 'false');
        if (podcastForm.video_file) {
          formData.append('video_file', podcastForm.video_file);
        }
        const updated = await adminPodcastsAPI.updatePodcastForm(editingPodcast.id, formData);
        setPodcasts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
        setSelectedPodcast((cur) => (cur?.id === updated.id ? updated : cur));
        setSuccess('Podcast updated successfully!');
      } else {
        const formData = new FormData();
        formData.append('name', podcastForm.name);
        formData.append('description', podcastForm.description || '');
        formData.append('video_file', podcastForm.video_file!);
        formData.append('is_free_public', podcastForm.is_free_public ? 'true' : 'false');
        const newPodcast = await adminPodcastsAPI.createPodcast(formData);
        setPodcasts((prev) => [newPodcast, ...prev]);
        setSuccess('Podcast created successfully!');
      }
      setShowPodcastModal(false);
      setEditingPodcast(null);
      resetPodcastForm();
    } catch (err: any) {
      setError(err.message || (editingPodcast ? 'Failed to update podcast' : 'Failed to create podcast'));
    } finally {
      setUploadingPodcast(false);
    }
  };

  const openEditPodcast = (podcast: Podcast) => {
    setEditingPodcast(podcast);
    setPodcastForm({
      name: podcast.name,
      description: podcast.description || '',
      video_file: null,
      is_free_public: Boolean(podcast.is_free_public),
    });
    setShowPodcastModal(true);
  };

  const handleTogglePodcastFreePublic = async (podcast: Podcast) => {
    setPodcastFreeToggleId(podcast.id);
    setError(null);
    try {
      const updated = await adminPodcastsAPI.updatePodcast(podcast.id, {
        is_free_public: !podcast.is_free_public,
      });
      setPodcasts((prev) => prev.map((p) => (p.id === podcast.id ? updated : p)));
      setSelectedPodcast((cur) => (cur?.id === podcast.id ? updated : cur));
      setSuccess(
        updated.is_free_public
          ? 'Podcast is available to all signed-in users (free).'
          : 'Podcast access updated.'
      );
    } catch (err: any) {
      setError(err.message || 'Failed to update podcast');
    } finally {
      setPodcastFreeToggleId(null);
    }
  };

  const handleDeletePodcast = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this podcast?')) {
      return;
    }

    setLoadingPodcasts(true);
    setError(null);
    try {
      await adminPodcastsAPI.deletePodcast(id);
      setPodcasts((prev) => prev.filter((p) => p.id !== id));
      setSuccess('Podcast deleted successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to delete podcast');
    } finally {
      setLoadingPodcasts(false);
    }
  };

  const resetPodcastForm = () => {
    setEditingPodcast(null);
    setPodcastForm({
      name: '',
      description: '',
      video_file: null,
      is_free_public: false,
    });
    const videoInput = document.getElementById('podcast-video-input') as HTMLInputElement;
    if (videoInput) videoInput.value = '';
  };

  // Admin Podcast Player handlers
  const handlePlayPausePodcast = () => {
    const video = adminVideoRef.current;
    if (!video || !selectedPodcast) return;
    
    if (isPlayingPodcast) {
      video.pause();
    } else {
      video.play();
    }
  };

  const handleSeekPodcast = (e: React.MouseEvent<HTMLDivElement>) => {
    const video = adminVideoRef.current;
    if (!video || !selectedPodcast) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = x / rect.width;
    const newTime = percent * video.duration;
    video.currentTime = newTime;
    setPodcastCurrentTime(newTime);
  };

  const togglePodcastMute = () => {
    const video = adminVideoRef.current;
    if (!video) return;
    
    video.muted = !isPodcastMuted;
    setIsPodcastMuted(!isPodcastMuted);
  };

  const handlePodcastVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = adminVideoRef.current;
    if (!video) return;
    
    const newVolume = parseFloat(e.target.value);
    video.volume = newVolume;
    setPodcastVolume(newVolume);
    setIsPodcastMuted(newVolume === 0);
  };

  const togglePodcastFullscreen = () => {
    const video = adminVideoRef.current;
    if (!video) return;
    
    if (!document.fullscreenElement) {
      video.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Setup video event listeners when selectedPodcast changes
  useEffect(() => {
    const video = adminVideoRef.current;
    if (!video || !selectedPodcast) return;

    const streamUrl = podcastsAPI.getStreamURL(selectedPodcast.id);
    video.src = streamUrl;
    video.load();

    const handleTimeUpdate = () => {
      setPodcastCurrentTime(video.currentTime);
    };

    const handleLoadedMetadata = () => {
      setPodcastDuration(video.duration);
    };

    const handlePlay = () => {
      setIsPlayingPodcast(true);
      setPodcastBuffering(false);
    };

    const handlePause = () => {
      setIsPlayingPodcast(false);
    };

    const handleWaiting = () => {
      setPodcastBuffering(true);
    };

    const handleCanPlay = () => {
      setPodcastBuffering(false);
    };

    const handleEnded = () => {
      setIsPlayingPodcast(false);
      setPodcastCurrentTime(0);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('ended', handleEnded);
    };
  }, [selectedPodcast]);

  const handleCreateSegment = async () => {
    if (!segmentForm.name.trim()) {
      setError('Segment name is required');
      return;
    }

    const { value: segNum, error: segErr } = parseOptionalNonNegativeInt(segmentForm.segment_number);
    if (segErr) {
      setError(segErr);
      return;
    }

    setLoadingSegments(true);
    setError(null);
    try {
      const newSegment = await structuralSegmentsAPI.createSegment({
        name: segmentForm.name,
        description: segmentForm.description || undefined,
        segment_number: segNum,
        test_category: segmentForm.test_category || undefined,
        is_active: segmentForm.is_active,
        is_free_public: segmentForm.is_free_public,
      });
      setSuccess('Segment created successfully!');
      setShowSegmentModal(false);
      resetSegmentForm();
      await refreshSegmentViews();
      try {
        const detail = await structuralSegmentsAPI.getSegment(newSegment.id);
        setExpandedSegmentId(newSegment.id);
        setExpandedSegmentDetail(detail);
      } catch {
        /* list refresh is enough */
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create segment');
    } finally {
      setLoadingSegments(false);
    }
  };

  const handleUpdateSegment = async (id: number) => {
    const { value: segNum, error: segErr } = parseOptionalNonNegativeInt(segmentForm.segment_number);
    if (segErr) {
      setError(segErr);
      return;
    }

    setLoadingSegments(true);
    setError(null);
    try {
      const updated = await structuralSegmentsAPI.updateSegment(id, {
        name: segmentForm.name,
        description: segmentForm.description || undefined,
        segment_number: segNum,
        test_category: segmentForm.test_category || undefined,
        is_active: segmentForm.is_active,
        is_free_public: segmentForm.is_free_public,
      });
      setEditingSegment(null);
      setShowSegmentModal(false);
      setSuccess('Segment updated successfully!');
      resetSegmentForm();
      await refreshSegmentViews();
      if (expandedSegmentId === id) {
        try {
          setExpandedSegmentDetail(await structuralSegmentsAPI.getSegment(id));
        } catch {
          /* ignore */
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update segment');
    } finally {
      setLoadingSegments(false);
    }
  };

  const handleDeleteSegment = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this segment? All associated audios will be removed.')) {
      return;
    }

    try {
      await structuralSegmentsAPI.deleteSegment(id);
      setSegments((prev) => prev.filter((s: StructuralSegment) => s.id !== id));
      setSuccess('Segment deleted successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to delete segment');
    }
  };

  const assignAudioToSegmentSlot = async (
    segmentId: number,
    order: number,
    audioFileId: number,
    segment?: StructuralSegment | null
  ) => {
    const seg =
      segment ??
      (expandedSegmentDetail?.id === segmentId ? expandedSegmentDetail : null) ??
      segments.find((s: StructuralSegment) => s.id === segmentId);
    const existing = seg ? getSegmentAudioAtOrder(seg, order) : undefined;
    if (existing) {
      await structuralSegmentsAPI.removeAudioFromSegment(segmentId, existing.id);
    }
    await structuralSegmentsAPI.addAudioToSegment(segmentId, {
      audio_file_id: audioFileId,
      order,
    });
    await refreshSegmentViews();
  };

  const openSegmentLibraryPicker = (segmentId: number, slotOrder: number) => {
    setSelectedSegmentForAudio(segmentId);
    setSegmentLibrarySlotOrder(slotOrder);
    setShowAddAudioModal(true);
  };

  const closeSegmentAudioModal = () => {
    if (uploadingAudios || segmentSlotUploadingOrder != null) return;
    setShowAddAudioModal(false);
    setSelectedSegmentForAudio(null);
    setSegmentLibrarySlotOrder(null);
    setSelectedAudioFiles([]);
    const fileInput = document.getElementById('segment-audio-file-input') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const handleSlotFileUpload = async (
    segmentId: number,
    order: number,
    file: File,
    segment?: StructuralSegment | null
  ) => {
    if (!file.type.startsWith('audio/')) {
      setError(`${file.name} is not an audio file`);
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setError(`${file.name} must be less than 50MB`);
      return;
    }

    const seg =
      segment ??
      (expandedSegmentDetail?.id === segmentId ? expandedSegmentDetail : null) ??
      segments.find((s: StructuralSegment) => s.id === segmentId);

    setSegmentSlotUploadingOrder(order);
    setError(null);
    try {
      const uploaded = await materialAPI.uploadMaterial({
        audio_file: file,
        test_category: seg?.test_category || undefined,
      });
      await assignAudioToSegmentSlot(segmentId, order, uploaded.id, seg);
      setSuccess(`Slot ${order + 1} updated successfully!`);
    } catch (err: any) {
      setError(err.message || 'Failed to upload audio for this slot');
    } finally {
      setSegmentSlotUploadingOrder(null);
    }
  };

  const handlePickAudioForSlot = async (audioId: number) => {
    if (!selectedSegmentForAudio || segmentLibrarySlotOrder == null) return;
    try {
      await assignAudioToSegmentSlot(
        selectedSegmentForAudio,
        segmentLibrarySlotOrder,
        audioId
      );
      setSuccess(`Slot ${segmentLibrarySlotOrder + 1} updated successfully!`);
      closeSegmentAudioModal();
    } catch (err: any) {
      setError(err.message || 'Failed to assign audio to slot');
    }
  };

  const isMaterialUsedInSegment = useCallback(
    (segment: StructuralSegment, materialId: number): boolean =>
      (segment.audios || []).some((sa: SegmentAudio) => sa.audio_file === materialId),
    []
  );

  const handleRemoveAudioFromSegment = async (segmentId: number, segmentAudioId: number) => {
    if (!window.confirm('Are you sure you want to remove this audio from the segment?')) {
      return;
    }

    try {
      await structuralSegmentsAPI.removeAudioFromSegment(segmentId, segmentAudioId);
      await refreshSegmentViews();
      setSuccess('Audio removed from segment successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to remove audio from segment');
    }
  };

  const handleEditSegment = (segment: StructuralSegment) => {
    setEditingSegment(segment);
    setSegmentForm({
      name: segment.name,
      description: segment.description || '',
      segment_number: segment.segment_number?.toString() || '',
      test_category: segment.test_category || '',
      is_active: segment.is_active,
      is_free_public: Boolean(segment.is_free_public),
    });
    setShowSegmentModal(true);
  };

  const resetSegmentForm = () => {
    setSegmentForm({
      name: '',
      description: '',
      segment_number: '',
      test_category: '',
      is_active: true,
      is_free_public: false,
    });
    setEditingSegment(null);
  };

  // Dialogue Categories functions
  const fetchDialogueCategories = async () => {
    setLoadingCategories(true);
    setError(null);
    try {
      const data = await adminDialogueCategoriesAPI.getCategories();
      setDialogueCategories(data);
      setCategoryFilter((prev) => (prev == null && data.length > 0 ? data[0].id : prev));
    } catch (err: any) {
      setError(err.message || 'Failed to fetch dialogue categories');
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchCategoryDialogues = async () => {
    const categoryId = categoryFilter ?? dialogueCategories[0]?.id;
    if (!categoryId || dialogueCategories.length === 0) {
      setCategoryDialogues([]);
      setDialogueTotalPages(1);
      setDialogueTotalCount(0);
      return;
    }
    setLoadingTopics(true);
    setError(null);
    try {
      const res = await adminDialogueCategoriesAPI.getDialogues(categoryId, {
        page: dialoguePage,
        page_size: 20,
        q: topicSearchTerm.trim() || undefined,
      });
      setCategoryDialogues(res.results);
      setDialogueTotalPages(res.total_pages);
      setDialogueTotalCount(res.count);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch category dialogues');
    } finally {
      setLoadingTopics(false);
    }
  };

  const refreshDialogueViews = async () => {
    await fetchCategoryDialogues();
    if (expandedDialogueId != null) {
      try {
        const detail = await adminDialogueCategoriesAPI.getDialogue(expandedDialogueId);
        setExpandedDialogueDetail(detail);
      } catch {
        setExpandedDialogueId(null);
        setExpandedDialogueDetail(null);
      }
    }
  };

  const toggleManageDialogue = async (id: number) => {
    if (expandedDialogueId === id) {
      setExpandedDialogueId(null);
      setExpandedDialogueDetail(null);
      return;
    }
    setExpandedDialogueId(id);
    setLoadingDialogueDetail(true);
    try {
      const detail = await adminDialogueCategoriesAPI.getDialogue(id);
      setExpandedDialogueDetail(detail);
    } catch (err: any) {
      setError(err.message || 'Failed to load dialogue details');
      setExpandedDialogueId(null);
      setExpandedDialogueDetail(null);
    } finally {
      setLoadingDialogueDetail(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'dialogues') {
      void fetchDialogueCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'dialogues') return;
    if (loadingCategories || dialogueCategories.length === 0) return;
    void fetchCategoryDialogues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, categoryFilter, dialoguePage, topicSearchTerm, dialogueCategories.length, loadingCategories]);

  const handleCreateCategory = async () => {
    if (!categoryForm.name.trim()) {
      setError('Category name is required');
      return;
    }

    setLoadingCategories(true);
    setError(null);
    try {
      const newCategory = await adminDialogueCategoriesAPI.createCategory({
        name: categoryForm.name,
        description: categoryForm.description || undefined,
        is_active: categoryForm.is_active,
      });
      setDialogueCategories((prev) => [...prev, newCategory]);
      setShowCategoryModal(false);
      setSuccess('Category created successfully!');
      resetCategoryForm();
    } catch (err: any) {
      setError(err.message || 'Failed to create category');
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleUpdateCategory = async (id: number) => {
    setLoadingCategories(true);
    setError(null);
    try {
      const updated = await adminDialogueCategoriesAPI.updateCategory(id, {
        name: categoryForm.name,
        description: categoryForm.description || undefined,
        is_active: categoryForm.is_active,
      });
      setDialogueCategories((prev) => prev.map((c: DialogueCategory) => (c.id === id ? updated : c)));
      setEditingCategory(null);
      setShowCategoryModal(false);
      setSuccess('Category updated successfully!');
      resetCategoryForm();
    } catch (err: any) {
      setError(err.message || 'Failed to update category');
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleDeleteCategory = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this category? All associated dialogues will be deleted.')) {
      return;
    }

    try {
      await adminDialogueCategoriesAPI.deleteCategory(id);
      setDialogueCategories((prev) => prev.filter((c: DialogueCategory) => c.id !== id));
      setSuccess('Category deleted successfully!');
      // Refresh dialogues after deleting category
      fetchDialogueCategories();
      refreshDialogueViews();
    } catch (err: any) {
      setError(err.message || 'Failed to delete category');
    }
  };

  const openDialogueBriefingFilePicker = (dialogueId: number, partId: number) => {
    dialogueBriefingPickRef.current = { dialogueId, partId };
    dialogueBriefingFileInputRef.current?.click();
  };

  const handleDialogueBriefingFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const pick = dialogueBriefingPickRef.current;
    dialogueBriefingPickRef.current = null;
    if (!pick || !file) return;

    const key = `${pick.dialogueId}-${pick.partId}`;
    setUploadingDialogueBriefingKey(key);
    setError(null);
    try {
      const uploaded = await materialAPI.uploadMaterial({ audio_file: file });
      await adminDialogueCategoriesAPI.updateDialoguePart(pick.dialogueId, pick.partId, {
        briefing_audio: uploaded.id,
      });
      setSuccess('Dialogue briefing audio updated. It plays before the first clip in this dialogue.');
      await refreshDialogueViews();
    } catch (err: any) {
      setError(err.message || 'Failed to upload briefing audio');
    } finally {
      setUploadingDialogueBriefingKey(null);
    }
  };

  const handleClearDialogueBriefing = async (dialogueId: number, partId: number) => {
    if (!window.confirm('Remove briefing audio from this part?')) {
      return;
    }
    setError(null);
    try {
      await adminDialogueCategoriesAPI.updateDialoguePart(dialogueId, partId, { briefing_audio: null });
      setSuccess('Briefing audio removed from dialogue.');
      await refreshDialogueViews();
    } catch (err: any) {
      setError(err.message || 'Failed to remove briefing');
    }
  };

  const handleCreateDialogue = async (cd: CategoryDialogue) => {
    const existing = cd.parts || [];
    const nextOrder =
      existing.length > 0 ? Math.max(...existing.map((d: DialoguePart) => d.order)) + 1 : 0;
    setError(null);
    try {
      await adminDialogueCategoriesAPI.createDialoguePart(cd.id, {
        name: `Part ${nextOrder + 1}`,
        order: nextOrder,
      });
      setSuccess('Part created.');
      await refreshDialogueViews();
    } catch (err: any) {
      setError(err.message || 'Failed to create part');
    }
  };

  const handleEditCategory = (category: DialogueCategory) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      description: category.description || '',
      is_active: category.is_active,
    });
    setShowCategoryModal(true);
  };

  const resetCategoryForm = () => {
    setCategoryForm({
      name: '',
      description: '',
      is_active: true,
    });
    setEditingCategory(null);
  };

  // Category dialogue CRUD
  const handleCreateTopic = async () => {
    if (!topicForm.name.trim()) {
      setError('Dialogue name is required');
      return;
    }
    const { value: categoryId, error: catErr } = parseRequiredPositiveInt(topicForm.category, 'Category');
    if (catErr) {
      setError(catErr);
      return;
    }

    setLoadingTopics(true);
    setError(null);
    try {
      const newTopic = await adminDialogueCategoriesAPI.createDialogue(categoryId, {
        category: categoryId,
        name: topicForm.name,
        description: topicForm.description || undefined,
        is_active: topicForm.is_active,
        is_free_public: topicForm.is_free_public,
        enable_as_mock: topicForm.enable_as_mock
      });
      setCategoryDialogues((prev) => [...prev, newTopic]);
      setShowTopicModal(false);
      setSuccess('Dialogue created successfully!');
      resetTopicForm();
      // Refresh categories to update dialogues_count
      fetchDialogueCategories();
    } catch (err: any) {
      setError(err.message || 'Failed to create dialogue');
    } finally {
      setLoadingTopics(false);
    }
  };

  const handleUpdateTopic = async (id: number) => {
    let categoryId: number | undefined;
    if (topicForm.category.trim()) {
      const parsed = parseRequiredPositiveInt(topicForm.category, 'Category');
      if (parsed.error) {
        setError(parsed.error);
        return;
      }
      categoryId = parsed.value;
    }

    setLoadingTopics(true);
    setError(null);
    try {
      const updated = await adminDialogueCategoriesAPI.updateDialogue(id, {
        category: categoryId,
        name: topicForm.name,
        description: topicForm.description || undefined,
        is_active: topicForm.is_active,
        is_free_public: topicForm.is_free_public,
        enable_as_mock: topicForm.enable_as_mock
      });
      setCategoryDialogues((prev) => prev.map((t: CategoryDialogue) => (t.id === id ? updated : t)));
      setEditingTopic(null);
      setShowTopicModal(false);
      setSuccess('Dialogue updated successfully!');
      resetTopicForm();
      // Refresh categories to update dialogues_count
      fetchDialogueCategories();
    } catch (err: any) {
      setError(err.message || 'Failed to update dialogue');
    } finally {
      setLoadingTopics(false);
    }
  };

  const handleDeleteTopic = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this dialogue? All associated audios will be removed.')) {
      return;
    }

    try {
      await adminDialogueCategoriesAPI.deleteDialogue(id);
      if (expandedDialogueId === id) {
        setExpandedDialogueId(null);
        setExpandedDialogueDetail(null);
      }
      setCategoryDialogues((prev) => prev.filter((t: CategoryDialogue) => t.id !== id));
      setSuccess('Dialogue deleted successfully!');
      // Refresh categories to update dialogues_count
      fetchDialogueCategories();
    } catch (err: any) {
      setError(err.message || 'Failed to delete dialogue');
    }
  };

  const handleEditTopic = (topic: CategoryDialogue) => {
    setEditingTopic(topic);
    setTopicForm({
      category: topic.category.toString(),
      name: topic.name,
      description: topic.description || '',
      is_active: topic.is_active,
      is_free_public: Boolean(topic.is_free_public),
      enable_as_mock: topic.enable_as_mock ?? false
    });
    setShowTopicModal(true);
  };

  const resetTopicForm = () => {
    setTopicForm({
      category: '',
      name: '',
      description: '',
      is_active: true,
      is_free_public: false,
      enable_as_mock: false
    });
    setEditingTopic(null);
    setSelectedCategoryForTopic(null);
  };

  const handleAddAudioToTopic = async (audioId: number) => {
    if (!selectedTopicForAudio || selectedDialogueForAudio == null) {
      setError('Select a part before adding audio.');
      return;
    }

    try {
      await adminDialogueCategoriesAPI.addAudioToDialoguePart(selectedTopicForAudio, {
        part_id: selectedDialogueForAudio,
        audio_file_id: audioId,
      });
      setSuccess('Audio added to part successfully!');
      // Refresh list to show updated audio count
      fetchDialogueCategories();
      refreshDialogueViews();
    } catch (err: any) {
      setError(err.message || 'Failed to add audio to part');
    }
  };

  const handleRemoveAudioFromTopic = async (dialogueId: number, linkId: number) => {
    try {
      await adminDialogueCategoriesAPI.removeDialoguePartAudio(dialogueId, linkId);
      setSuccess('Audio removed from dialogue successfully!');
      fetchDialogueCategories();
      refreshDialogueViews();
    } catch (err: any) {
      setError(err.message || 'Failed to remove audio');
    }
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
  };

  const renderDialoguePartsPanel = (cd: CategoryDialogue) => (
    <>
      <div className="segment-audios">
        <div className="segment-audios-header">
          <h4>Parts &amp; audios ({cd.audio_count})</h4>
          <button
            type="button"
            className="add-audio-btn"
            onClick={() => void handleCreateDialogue(cd)}
            title="Add part"
          >
            {React.createElement(FiPlus as React.ComponentType<{ size?: number }>, { size: 16 })}
          </button>
        </div>
        {(!cd.parts || cd.parts.length === 0) ? (
          <p className="no-audios">No parts yet. Click + to add a part, then add briefing and voices per part.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            {[...(cd.parts || [])]
              .sort((a: DialoguePart, b: DialoguePart) => a.order - b.order)
              .map((dlg: DialoguePart) => {
                const bKey = `${cd.id}-${dlg.id}`;
                const uploadingB = uploadingDialogueBriefingKey === bKey;
                return (
                  <div
                    key={dlg.id}
                    style={{
                      border: '1px solid #333',
                      borderRadius: '8px',
                      padding: '0.65rem 0.75rem',
                      background: 'rgba(0,0,0,0.2)',
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: '0.35rem', color: '#fff' }}>
                      {dlg.name?.trim() || `Part ${dlg.order + 1}`}
                    </div>
                    <div
                      style={{
                        fontSize: '0.78rem',
                        color: '#aaa',
                        marginBottom: '0.5rem',
                        padding: '0.45rem',
                        background: 'rgba(230, 57, 70, 0.06)',
                        borderRadius: '6px',
                      }}
                    >
                      <div style={{ marginBottom: '0.25rem', color: '#ccc' }}>Briefing (before 1st clip)</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center' }}>
                        <span style={{ color: '#888' }}>
                          {dlg.briefing_audio_detail?.file_name || 'Not set'}
                        </span>
                        <button
                          type="button"
                          className="action-btn edit-btn"
                          style={{ fontSize: '0.72rem' }}
                          disabled={uploadingB}
                          onClick={() => openDialogueBriefingFilePicker(cd.id, dlg.id)}
                        >
                          {uploadingB ? '…' : 'Upload'}
                        </button>
                        {dlg.briefing_audio != null && (
                          <button
                            type="button"
                            className="action-btn delete-btn"
                            style={{ fontSize: '0.72rem' }}
                            disabled={uploadingB}
                            onClick={() => void handleClearDialogueBriefing(cd.id, dlg.id)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="segment-audios-list">
                      {(dlg.audios || [])
                        .sort((a: DialoguePartAudio, b: DialoguePartAudio) => a.order - b.order)
                        .map((audioLink: DialoguePartAudio) => (
                          <div key={audioLink.id} className="segment-audio-item">
                            <span className="audio-order">{audioLink.order + 1}.</span>
                            <span className="audio-name">{audioLink.audio_file_data.file_name}</span>
                            <div className="audio-actions">
                              <button
                                type="button"
                                className="edit-content-btn"
                                style={{ fontSize: '0.72rem', marginRight: '0.35rem', padding: '0.2rem 0.45rem' }}
                                onClick={() => setEditingContentAudioId(audioLink.audio_file_data.id)}
                                title="Source text & AI translations (OpenAI), same as Lab"
                              >
                                Content
                              </button>
                              <button
                                className="action-btn-small delete-btn"
                                onClick={() => handleRemoveAudioFromTopic(cd.id, audioLink.id)}
                                title="Remove"
                              >
                                {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 14 })}
                              </button>
                            </div>
                          </div>
                        ))}
                    </div>
                    <button
                      type="button"
                      className="add-audio-btn"
                      style={{ marginTop: '0.35rem' }}
                      onClick={() => {
                        setSelectedTopicForAudio(cd.id);
                        setSelectedDialogueForAudio(dlg.id);
                        setShowAddAudioToTopicModal(true);
                      }}
                      title="Add audio to this part"
                    >
                      {React.createElement(FiPlus as React.ComponentType<{ size?: number }>, { size: 14 })}
                      <span style={{ marginLeft: '0.25rem', fontSize: '0.8rem' }}>Add voice</span>
                    </button>
                    {(dlg.audios || []).length > 0 && (
                      <button
                        type="button"
                        className="edit-content-btn"
                        style={{ marginTop: '0.35rem', marginLeft: '0.5rem', fontSize: '0.78rem' }}
                        onClick={() =>
                          setBulkContentPart({
                            topicId: cd.id,
                            partId: dlg.id,
                            partName: dlg.name?.trim() || `Part ${dlg.order + 1}`,
                            audios: [...(dlg.audios || [])].sort(
                              (a, b) => a.order - b.order,
                            ),
                          })
                        }
                        title="Paste dialogue transcript, auto-match voices, bulk translations"
                      >
                        Bulk content
                      </button>
                    )}
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </>
  );

  const renderSegmentAudiosPanel = (segment: StructuralSegment) => {
    const filledCount = (segment.audios || []).length;
    const slotDefs = STRUCTURAL_SEGMENT_AUDIO_SLOTS;

    return (
      <div className="segment-audios mm-segment-audios-slots">
        <div className="mm-segment-slots-header">
          <div>
            <h4>Audio slots</h4>
            <p className="mm-segment-slots-sub">
              Three clips required: 1 prompt + 2 model answers. Students hear the prompt on Start, then answers after Next.
            </p>
          </div>
          <span
            className={`mm-segment-slots-progress ${filledCount >= 3 ? 'mm-segment-slots-progress--complete' : ''}`}
          >
            {filledCount}/3 filled
          </span>
        </div>

        <div className="mm-segment-slot-grid">
          {slotDefs.map((slot) => {
            const segmentAudio = getSegmentAudioAtOrder(segment, slot.order);
            const isUploading = segmentSlotUploadingOrder === slot.order;
            const inputId = `segment-slot-file-${segment.id}-${slot.order}`;

            return (
              <div
                key={slot.order}
                className={`mm-segment-slot-card ${segmentAudio ? 'mm-segment-slot-card--filled' : ''} mm-segment-slot-card--${slot.role}`}
              >
                <div className="mm-segment-slot-card-head">
                  <span className="mm-segment-slot-index">{slot.order + 1}</span>
                  <div className="mm-segment-slot-titles">
                    <strong>{slot.label}</strong>
                    <span className="mm-segment-slot-desc">{slot.description}</span>
                  </div>
                  <span className={`mm-segment-slot-badge mm-segment-slot-badge--${slot.role}`}>
                    {slot.shortLabel}
                  </span>
                </div>

                {segmentAudio ? (
                  <div className="mm-segment-slot-filled">
                    <div className="mm-segment-slot-file">
                      {React.createElement(FiMusic as React.ComponentType<{ size?: number }>, { size: 18 })}
                      <span className="mm-segment-slot-filename">
                        {segmentAudio.audio_file_data.file_name}
                      </span>
                    </div>
                    <div className="mm-segment-slot-actions">
                      <button
                        type="button"
                        className="edit-content-btn"
                        onClick={() => setEditingContentAudioId(segmentAudio.audio_file_data.id)}
                        title="Source text & translations"
                      >
                        Content
                      </button>
                      <label className="mm-segment-slot-upload-btn" title="Replace with new upload">
                        <input
                          type="file"
                          accept="audio/*"
                          className="mm-segment-slot-file-input"
                          disabled={isUploading}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) void handleSlotFileUpload(segment.id, slot.order, file, segment);
                            e.target.value = '';
                          }}
                        />
                        {isUploading ? 'Uploading…' : 'Replace'}
                      </label>
                      <button
                        type="button"
                        className="mm-segment-slot-library-btn"
                        onClick={() => openSegmentLibraryPicker(segment.id, slot.order)}
                        disabled={isUploading}
                      >
                        From library
                      </button>
                      <button
                        type="button"
                        className="action-btn-small delete-btn"
                        onClick={() => handleRemoveAudioFromSegment(segment.id, segmentAudio.id)}
                        title="Remove"
                        disabled={isUploading}
                      >
                        {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 14 })}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mm-segment-slot-empty">
                    <p>No audio assigned</p>
                    <div className="mm-segment-slot-actions">
                      <label className="mm-segment-slot-upload-btn mm-segment-slot-upload-btn--primary">
                        <input
                          id={inputId}
                          type="file"
                          accept="audio/*"
                          className="mm-segment-slot-file-input"
                          disabled={isUploading}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) void handleSlotFileUpload(segment.id, slot.order, file, segment);
                            e.target.value = '';
                          }}
                        />
                        {isUploading ? (
                          <>
                            {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, {
                              className: 'spinner',
                              size: 16,
                            })}
                            Uploading…
                          </>
                        ) : (
                          <>
                            {React.createElement(FiUpload as React.ComponentType<{ size?: number }>, { size: 16 })}
                            Upload file
                          </>
                        )}
                      </label>
                      <button
                        type="button"
                        className="mm-segment-slot-library-btn"
                        onClick={() => openSegmentLibraryPicker(segment.id, slot.order)}
                        disabled={isUploading}
                      >
                        {React.createElement(FiFolder as React.ComponentType<{ size?: number }>, { size: 16 })}
                        From library
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.4, 0, 0.2, 1] as const
      }
    }
  };

  return (
    <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <AdminSidebar />
      
      <div className="dashboard-main">
        <DashboardHeader
          userEmail={userInfo.email}
          organization={userInfo.organization}
          onLogout={handleLogout}
        />
        
        <div className="dashboard-content">
          <div className="dashboard-content-inner">
            <motion.div
              className="dashboard-welcome"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div>
                <h1 className="dashboard-title">Material Management</h1>
                <p className="dashboard-subtitle">Manage audio materials and structural segments</p>
              </div>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                {activeTab === 'segments' && (
                  <motion.button
                    className="upload-btn"
                    onClick={() => {
                      resetSegmentForm();
                      setShowSegmentModal(true);
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {React.createElement(FiPlus as React.ComponentType<{ size?: number }>, { size: 20 })}
                    Create Segment
                  </motion.button>
                )}
                {activeTab === 'podcasts' && (
                  <motion.button
                    className="upload-btn"
                    onClick={() => {
                      resetPodcastForm();
                      setEditingPodcast(null);
                      setShowPodcastModal(true);
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {React.createElement(FiPlus as React.ComponentType<{ size?: number }>, { size: 20 })}
                    Add Podcast
                  </motion.button>
                )}
                {activeTab === 'dialogues' && (
                  <>
                    <motion.button
                      className="upload-btn"
                      onClick={() => {
                        resetCategoryForm();
                        setShowCategoryModal(true);
                      }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {React.createElement(FiPlus as React.ComponentType<{ size?: number }>, { size: 20 })}
                      Create Category
                    </motion.button>
                    <motion.button
                      className="upload-btn"
                      onClick={() => {
                        resetTopicForm();
                        setShowTopicModal(true);
                      }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      style={{ marginLeft: '1rem' }}
                    >
                      {React.createElement(FiPlus as React.ComponentType<{ size?: number }>, { size: 20 })}
                      Create dialogue
                    </motion.button>
                  </>
                )}
              </div>
            </motion.div>

            {/* Tabs */}
            <div className="material-tabs">
              <button
                className={`tab-button ${activeTab === 'materials' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('materials');
                  setSelectedSegment(null);
                }}
              >
                {React.createElement(FiMusic as React.ComponentType<{ size?: number }>, { size: 18 })}
                Materials
              </button>
              <button
                className={`tab-button ${activeTab === 'dialogues' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('dialogues');
                  setSelectedSegment(null);
                }}
              >
                {React.createElement(FiFolder as React.ComponentType<{ size?: number }>, { size: 18 })}
                Dialogue Categories
              </button>
              <button
                className={`tab-button ${activeTab === 'segments' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('segments');
                  setSelectedSegment(null);
                }}
              >
                {React.createElement(FiLayers as React.ComponentType<{ size?: number }>, { size: 18 })}
                Structural Segments
              </button>
              <button
                className={`tab-button ${activeTab === 'podcasts' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('podcasts');
                  setSelectedSegment(null);
                }}
              >
                {React.createElement(FiPlay as React.ComponentType<{ size?: number }>, { size: 18 })}
                Podcasts
              </button>
            </div>

            {error && (
              <motion.div
                className="alert alert-error"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={() => setError(null)}
              >
                {error}
              </motion.div>
            )}

            {success && (
              <motion.div
                className="alert alert-success"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={() => setSuccess(null)}
              >
                {success}
              </motion.div>
            )}

            {/* Content based on active tab */}
            {activeTab === 'materials' && (
              <>
            <motion.div
              className="material-filters mm-chime-card"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '1rem', justifyContent: 'space-between' }}>
                <div style={{ flex: '1 1 280px' }}>
                  <h3 style={{ margin: '0 0 0.35rem 0', color: '#fff', fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {React.createElement(FiVolume2 as React.ComponentType<{ size?: number }>, { size: 20 })}
                    Autoplay chime
                  </h3>
                  <p style={{ margin: 0, color: '#aaa', fontSize: '0.85rem', lineHeight: 1.5 }}>
                    Short sound played immediately after each examiner clip ends and right before the user&apos;s recording
                    starts (Autoplay on: structural segments, dialogue categories, or mock). Upload a small notification-style
                    clip (MP3, WAV, etc.).
                  </p>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    ref={autoplayChimeFileRef}
                    type="file"
                    accept="audio/*"
                    style={{ display: 'none' }}
                    onChange={(e) => void handleAutoplayChimeFileSelected(e)}
                  />
                  <button
                    type="button"
                    className="upload-btn-small"
                    disabled={autoplayChimeSaving || autoplayChimeAdminLoading}
                    onClick={() => autoplayChimeFileRef.current?.click()}
                  >
                    {autoplayChimeSaving ? 'Saving…' : 'Upload chime'}
                  </button>
                  <button
                    type="button"
                    className="action-btn-small delete-btn"
                    disabled={autoplayChimeSaving || !autoplayChimeAdmin?.autoplay_chime_audio_id}
                    onClick={() => void handleClearAutoplayChime()}
                  >
                    Clear
                  </button>
                </div>
              </div>
              {autoplayChimeAdminLoading ? (
                <p style={{ margin: '0.75rem 0 0 0', color: '#888', fontSize: '0.85rem' }}>Loading chime settings…</p>
              ) : autoplayChimeAdmin?.autoplay_chime_audio_id ? (
                <div style={{ marginTop: '0.75rem' }}>
                  <p style={{ margin: '0 0 0.35rem 0', color: '#ccc', fontSize: '0.88rem' }}>
                    Current: <strong>{autoplayChimeAdmin.file_name || `Audio #${autoplayChimeAdmin.autoplay_chime_audio_id}`}</strong>
                  </p>
                  {autoplayChimePreviewSrc ? (
                    <audio
                      controls
                      src={autoplayChimePreviewSrc}
                      style={{ width: '100%', maxWidth: '420px', height: 36 }}
                    />
                  ) : (
                    <p style={{ margin: 0, color: '#888', fontSize: '0.85rem' }}>
                      Preview needs a saved login session (token). Log in again if play stays at 0:00.
                    </p>
                  )}
                </div>
              ) : (
                <p style={{ margin: '0.75rem 0 0 0', color: '#888', fontSize: '0.85rem' }}>
                  No chime set — autoplay will start recording immediately after each examiner clip (no sound cue).
                </p>
              )}
            </motion.div>

            <motion.div
              className="material-filters mm-chime-card"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <div style={{ marginBottom: '0.5rem' }}>
                <h3
                  style={{
                    margin: '0 0 0.35rem 0',
                    color: '#fff',
                    fontSize: '1.05rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  {React.createElement(FiFileText as React.ComponentType<{ size?: number }>, { size: 20 })}
                  Test Rules PDFs (Garage)
                </h3>
                <p style={{ margin: 0, color: '#aaa', fontSize: '0.85rem', lineHeight: 1.5 }}>
                  Two PDFs for the learner <strong>Test Rules</strong> page. Stored in Garage; downloads use fixed names{' '}
                  <code style={{ color: '#ccc' }}>ccl-instructions.pdf</code> and{' '}
                  <code style={{ color: '#ccc' }}>naati-identification.pdf</code> so links stay stable.
                </p>
              </div>
              {testRulesPdfsLoading ? (
                <p style={{ margin: '0.5rem 0 0 0', color: '#888', fontSize: '0.85rem' }}>Loading PDF status…</p>
              ) : (
                <div
                  style={{
                    marginTop: '0.75rem',
                    display: 'grid',
                    gap: '1rem',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                  }}
                >
                  {TEST_RULES_PDF_SLOTS.map((slot) => {
                    const info = testRulesPdfs?.[slot.name] ?? null;
                    const saving = testRulesPdfSaving === slot.name;
                    return (
                      <div
                        key={slot.name}
                        style={{
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: 8,
                          padding: '0.85rem 1rem',
                          background: 'rgba(0,0,0,0.2)',
                        }}
                      >
                        <div style={{ fontWeight: 600, color: '#fff', marginBottom: '0.25rem' }}>{slot.title}</div>
                        <p style={{ margin: '0 0 0.5rem 0', color: '#888', fontSize: '0.8rem', lineHeight: 1.45 }}>
                          {slot.blurb}
                        </p>
                        <code style={{ fontSize: '0.75rem', color: '#aaa' }}>{slot.name}</code>
                        {info ? (
                          <p style={{ margin: '0.5rem 0 0 0', color: '#bbb', fontSize: '0.82rem' }}>
                            {formatFileSize(info.file_size)}
                            {info.updated_at
                              ? ` · updated ${new Date(info.updated_at).toLocaleString()}`
                              : ''}
                          </p>
                        ) : (
                          <p style={{ margin: '0.5rem 0 0 0', color: '#c9a227', fontSize: '0.82rem' }}>
                            Not uploaded — learners will see an error if they download.
                          </p>
                        )}
                        <input
                          ref={(el) => {
                            testRulesPdfInputRefs.current[slot.name] = el;
                          }}
                          type="file"
                          accept=".pdf,application/pdf"
                          style={{ display: 'none' }}
                          onChange={(e) => void handleTestRulesPdfSelected(slot.name, e)}
                        />
                        <button
                          type="button"
                          className="upload-btn-small"
                          style={{ marginTop: '0.65rem' }}
                          disabled={!!testRulesPdfSaving}
                          onClick={() => testRulesPdfInputRefs.current[slot.name]?.click()}
                        >
                          {saving ? 'Uploading…' : info ? 'Replace PDF' : 'Upload PDF'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>

            <div className="mm-materials-intro">
              <div className="mm-materials-intro-text">
                <h2 className="mm-materials-intro-title">System audio library</h2>
                <p className="mm-materials-intro-body">
                  Shared examiner/reference clips on the server. They appear in <strong>Structural Segments</strong> and{' '}
                  <strong>Dialogue Categories</strong> (and Lab). Upload new files when you add audio inside a segment or dialogue.
                  Here you can preview everything, open <strong>Content</strong> for captions and AI translations, set the{' '}
                  <strong>autoplay chime</strong>, and manage <strong>Test Rules PDFs</strong> (Garage) above.
                </p>
              </div>
              <button
                type="button"
                className="mm-materials-cta"
                onClick={() => {
                  setActiveTab('segments');
                  setSelectedSegment(null);
                }}
              >
                {React.createElement(FiUpload as React.ComponentType<{ size?: number }>, { size: 18 })}
                Upload in Segments
              </button>
            </div>

            <div className="mm-stat-grid">
              <div className="mm-stat-card">
                <span className="mm-stat-value">{materialsLibraryStats.total}</span>
                <span className="mm-stat-label">Files in library</span>
              </div>
              <div className="mm-stat-card">
                <span className="mm-stat-value">{materialsLibraryStats.assigned}</span>
                <span className="mm-stat-label">Linked to a segment</span>
              </div>
              <div className="mm-stat-card">
                <span className="mm-stat-value">{materialsLibraryStats.unassigned}</span>
                <span className="mm-stat-label">Not in any segment</span>
              </div>
              <div className="mm-stat-card">
                <span className="mm-stat-value">{materialsLibraryStats.active}</span>
                <span className="mm-stat-label">Active</span>
              </div>
              <div className="mm-stat-card mm-stat-card-wide">
                <span className="mm-stat-value">{formatFileSize(materialsLibraryStats.bytes)}</span>
                <span className="mm-stat-label">Combined size</span>
              </div>
              <div className="mm-stat-card">
                <span className="mm-stat-value">{categories.length}</span>
                <span className="mm-stat-label">Test categories (tags)</span>
              </div>
            </div>

            <motion.div
              className="material-filters mm-materials-toolbar"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <div className="mm-view-toggle" role="group" aria-label="Library layout">
                <button
                  type="button"
                  className={`mm-view-toggle-btn${materialsLibraryMode === 'by_segment' ? ' mm-view-toggle-btn-active' : ''}`}
                  onClick={() => setMaterialsLibraryMode('by_segment')}
                >
                  {React.createElement(FiLayers as React.ComponentType<{ size?: number }>, { size: 16 })}
                  By segment
                </button>
                <button
                  type="button"
                  className={`mm-view-toggle-btn${materialsLibraryMode === 'all_files' ? ' mm-view-toggle-btn-active' : ''}`}
                  onClick={() => {
                    setMaterialsLibraryMode('all_files');
                    setSelectedSegment(null);
                  }}
                >
                  {React.createElement(FiList as React.ComponentType<{ size?: number }>, { size: 16 })}
                  All files
                </button>
              </div>
              <div className="filter-group mm-toolbar-search">
                <div className="search-box">
                  {React.createElement(FiSearch as React.ComponentType<{ size?: number }>, { size: 18 })}
                  <input
                    type="text"
                    placeholder={
                      materialsLibraryMode === 'all_files'
                        ? 'Search filename, category, segment name…'
                        : 'Search segments or filenames…'
                    }
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            </motion.div>

            {/* Materials list */}
            {loading ? (
              <div className="loading-container">
                {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, { className: 'spinner', size: 32 })}
                <p>Loading materials...</p>
              </div>
            ) : materialsLibraryMode === 'all_files' ? (
              <div className="mm-all-files-wrap">
                {materials.length === 0 ? (
                  <div className="mm-empty-library">
                    {React.createElement(FiHardDrive as React.ComponentType<{ size?: number }>, { size: 52 })}
                    <p>No audio files in the library yet.</p>
                    <p className="mm-empty-hint">Add clips from the Structural Segments tab (Add audio → upload or pick Lab / materials).</p>
                    <button
                      type="button"
                      className="upload-btn-small"
                      onClick={() => {
                        setActiveTab('segments');
                        setSelectedSegment(null);
                      }}
                    >
                      Go to Structural Segments
                    </button>
                  </div>
                ) : allMaterialsFiltered.length === 0 ? (
                  <div className="mm-empty-library">
                    {React.createElement(FiSearch as React.ComponentType<{ size?: number }>, { size: 48 })}
                    <p>No files match your search.</p>
                  </div>
                ) : (
                  <>
                    <div className="mm-all-files-banner">
                      {React.createElement(FiHardDrive as React.ComponentType<{ size?: number }>, { size: 18 })}
                      <span>
                        Showing <strong>{allMaterialsFiltered.length}</strong> of {materials.length} files
                      </span>
                    </div>
                    <div className="materials-grid mm-materials-grid-wide">
                    {allMaterialsFiltered.map((material) => (
                      <motion.div
                        key={material.id}
                        className="material-card mm-material-card"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        whileHover={{ y: -4, boxShadow: '0 8px 20px rgba(0,0,0,0.25)' }}
                      >
                        <div className="mm-segment-pill">
                          {materialIdToSegmentName.get(material.id) ?? 'Not linked to a segment'}
                        </div>
                        <div className="material-card-header">
                          <div className="material-icon">
                            {React.createElement(FiMusic as React.ComponentType<{ size?: number }>, { size: 18 })}
                          </div>
                          <div className="material-info">
                            <h3 className="material-name">{material.file_name}</h3>
                            <div className="material-meta">
                              <span>{formatFileSize(material.file_size)}</span>
                              {material.test_category && (
                                <span className="material-category">{material.test_category}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="material-actions">
                          <button
                            className="play-btn"
                            onClick={() => handlePlayPause(material)}
                            title={playingId === material.id ? 'Pause' : 'Play'}
                          >
                            {playingId === material.id ? (
                              React.createElement(FiPause as React.ComponentType<{ size?: number }>, { size: 14 })
                            ) : (
                              React.createElement(FiPlay as React.ComponentType<{ size?: number }>, { size: 14 })
                            )}
                          </button>
                          <button
                            type="button"
                            className="edit-content-btn"
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                            title="Source text & AI translations"
                            onClick={() => setEditingContentAudioId(material.id)}
                          >
                            Content
                          </button>
                          <button
                            type="button"
                            className="action-btn-small edit-btn"
                            title="Use this file as the autoplay chime"
                            disabled={autoplayChimeSaving || !material.is_active}
                            onClick={() => void handleAssignMaterialAsAutoplayChime(material.id)}
                          >
                            Chime
                          </button>
                          <span className={`material-status ${material.is_active ? 'active' : 'inactive'}`}>
                            {material.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="segments-list-container">
                <div className="segments-sidebar mm-segments-sidebar">
                  <h3 className="mm-segments-sidebar-title">By segment</h3>
                  <p className="mm-segments-sidebar-hint">Groups files already attached to a structural segment.</p>
                  {materials.length === 0 ? (
                    <div className="empty-segments">
                      <p style={{ color: '#888', textAlign: 'center' }}>No materials found</p>
                      <p style={{ color: '#666', textAlign: 'center', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                        Upload materials from Structural Segments tab
                      </p>
                    </div>
                  ) : filteredSegmentIds.length === 0 ? (
                    <div className="empty-segments">
                      <p style={{ color: '#888', textAlign: 'center' }}>No segments match your search</p>
                    </div>
                  ) : (
                    <div className="segments-list">
                      {filteredSegmentIds.map((segmentId) => {
                        const segmentMaterials = materialsBySegment[segmentId];
                        if (!segmentMaterials || segmentMaterials.length === 0) return null;
                        
                        const isSelected = selectedSegment === segmentId;
                        const totalCount = segmentMaterials.length;

                        // Find matching structural segment by ID
                        let matchingSegment: StructuralSegment | undefined;
                        if (segmentId === 0) {
                          // Unassigned materials
                          matchingSegment = undefined;
                        } else {
                          matchingSegment = segments.find((s: StructuralSegment) => s.id === segmentId && s.is_active);
                        }
                        
                        // Get segment name - ALWAYS prefer structural segment name first
                        let segmentName = `Segment ${segmentId}`;
                        
                        if (matchingSegment) {
                          // Use structural segment name if found
                          segmentName = matchingSegment.name;
                        } else if (segmentId === 0) {
                          // For unassigned materials, try test_category or filename
                          const category = segmentMaterials[0]?.test_category;
                          if (category) {
                            segmentName = category;
                          } else {
                            // Try to extract name from file name
                            const firstFileName = segmentMaterials[0]?.file_name;
                            if (firstFileName) {
                              // Remove extension
                              const nameWithoutExt = firstFileName.replace(/\.[^/.]+$/, '');
                              // Extract meaningful part (before first dash or underscore, or use whole name)
                              const parts = nameWithoutExt.split(/[-_]/);
                              if (parts.length > 0 && parts[0].trim().length > 0) {
                                segmentName = parts[0].trim();
                              } else if (nameWithoutExt.trim().length > 0) {
                                segmentName = nameWithoutExt.trim();
                              } else {
                                segmentName = 'Unassigned';
                              }
                            } else {
                              segmentName = 'Unassigned';
                            }
                          }
                        } else if (segmentMaterials[0]?.test_category) {
                          segmentName = segmentMaterials[0].test_category;
                        }
                        
                        return (
                          <div
                            key={segmentId}
                            className={`segment-item ${isSelected ? 'selected' : ''}`}
                            onClick={() => setSelectedSegment(segmentId)}
                          >
                            <div className="segment-item-header">
                              <h4 className="segment-item-title">
                                {segmentName}
                              </h4>
                              <span className="segment-item-count">{totalCount} audio(s)</span>
                            </div>
                            {segmentMaterials[0]?.test_category && !matchingSegment && (
                              <span className="segment-item-category">
                                {segmentMaterials[0].test_category}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Selected Segment Materials */}
                <div className="segment-materials-content">
                  {selectedSegment === null ? (
                    <div className="empty-selection mm-empty-selection-hint">
                      {React.createElement(FiMusic as React.ComponentType<{ size?: number }>, { size: 48 })}
                      <p>Select a segment in the list to preview its audio files.</p>
                      <p className="mm-empty-selection-sub">
                        Or use <strong>All files</strong> above to search the whole library at once.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="segment-header">
                        <h2>
                          {(() => {
                            // Find matching structural segment by ID
                            let matchingSegment: StructuralSegment | undefined;
                            if (selectedSegment === 0) {
                              matchingSegment = undefined;
                            } else {
                              matchingSegment = segments.find((s: StructuralSegment) => s.id === selectedSegment && s.is_active);
                            }
                            
                            if (matchingSegment) {
                              return `${matchingSegment.name} - Audio Files`;
                            }
                            
                            if (selectedSegment === 0) {
                              const category = selectedSegmentMaterials[0]?.test_category;
                              if (category) {
                                return `${category} - Materials`;
                              }
                              // Try to get name from first file
                              const firstFileName = selectedSegmentMaterials[0]?.file_name;
                              if (firstFileName) {
                                const nameWithoutExt = firstFileName.replace(/\.[^/.]+$/, '');
                                const extractedName = nameWithoutExt.split('-')[0] || nameWithoutExt.split('_')[0] || nameWithoutExt;
                                return extractedName ? `${extractedName} - Materials` : 'Unassigned Materials';
                              }
                              return 'Unassigned Materials';
                            }
                            
                            const category = selectedSegmentMaterials[0]?.test_category;
                            if (category) {
                              return `${category} - Audio Files`;
                            }
                            return `Segment ${selectedSegment} - Audio Files`;
                          })()}
                        </h2>
                        <span className="segment-materials-count">
                          {selectedSegmentMaterials.length} file(s)
                        </span>
                      </div>
                      {selectedSegmentMaterials.length === 0 ? (
                        <div className="empty-state">
                          <p>No audio files in this segment</p>
                        </div>
                      ) : (
                        <div className="materials-grid">
                          {selectedSegmentMaterials.map((material) => (
                            <motion.div
                              key={material.id}
                              className="material-card"
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              whileHover={{ y: -5, boxShadow: '0 8px 16px rgba(0,0,0,0.2)' }}
                            >
                              <div className="material-card-header">
                                <div className="material-icon">
                                  {React.createElement(FiMusic as React.ComponentType<{ size?: number }>, { size: 18 })}
                                </div>
                                <div className="material-info">
                                  <h3 className="material-name">{material.file_name}</h3>
                                  <div className="material-meta">
                                    <span>{formatFileSize(material.file_size)}</span>
                                    {material.test_category && (
                                      <span className="material-category">{material.test_category}</span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div className="material-actions">
                                <button
                                  className="play-btn"
                                  onClick={() => handlePlayPause(material)}
                                  title={playingId === material.id ? 'Pause' : 'Play'}
                                >
                                  {playingId === material.id ? (
                                    React.createElement(FiPause as React.ComponentType<{ size?: number }>, { size: 14 })
                                  ) : (
                                    React.createElement(FiPlay as React.ComponentType<{ size?: number }>, { size: 14 })
                                  )}
                                </button>
                                <button
                                  type="button"
                                  className="edit-content-btn"
                                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                  title="Source text & AI translations"
                                  onClick={() => setEditingContentAudioId(material.id)}
                                >
                                  Content
                                </button>
                                <button
                                  type="button"
                                  className="action-btn-small edit-btn"
                                  title="Use this file as the autoplay chime"
                                  disabled={autoplayChimeSaving || !material.is_active}
                                  onClick={() => void handleAssignMaterialAsAutoplayChime(material.id)}
                                >
                                  Chime
                                </button>
                                <span className={`material-status ${material.is_active ? 'active' : 'inactive'}`}>
                                  {material.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
              </>
            )}

            {activeTab === 'segments' && (
                <>
                  <motion.div
                    className="material-filters"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    <div className="filter-group">
                      <div className="search-box">
                        {React.createElement(FiSearch as React.ComponentType<{ size?: number }>, { size: 18 })}
                        <input
                          type="text"
                          placeholder="Search segments by name, description, category..."
                          value={segmentSearchTerm}
                          onChange={(e) => {
                            setSegmentPage(1);
                            setSegmentSearchTerm(e.target.value);
                          }}
                        />
                      </div>
                    </div>
                  </motion.div>

                  <p className="dialogue-admin-hint">
                    Segments load 20 per page. Click Manage to edit audios in a segment.
                  </p>

                  {loadingSegments ? (
                    <div className="loading-container">
                      {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, { className: 'spinner', size: 32 })}
                      <p>Loading segments...</p>
                    </div>
                  ) : segmentListPage.length === 0 ? (
                    <div className="empty-state">
                      {React.createElement(FiLayers as React.ComponentType<{ size?: number }>, { size: 48 })}
                      <p>{segmentSearchTerm.trim() ? 'No segments match your search.' : 'No segments found'}</p>
                      {!segmentSearchTerm.trim() && (
                        <button className="upload-btn-small" onClick={() => {
                          resetSegmentForm();
                          setShowSegmentModal(true);
                        }}>
                          Create Your First Segment
                        </button>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="dialogue-admin-table-wrap">
                        <table className="dialogue-admin-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Category</th>
                              <th>Status</th>
                              <th>Audios</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {segmentListPage.map((segment: StructuralSegment) => (
                              <tr
                                key={segment.id}
                                className={expandedSegmentId === segment.id ? 'dialogue-row-active' : undefined}
                              >
                                <td>
                                  <strong>{segment.name}</strong>
                                  {segment.segment_number != null && (
                                    <div style={{ color: '#888', fontSize: '0.8rem' }}># {segment.segment_number}</div>
                                  )}
                                </td>
                                <td>{segment.test_category || '—'}</td>
                                <td>
                                  <span className={`segment-status ${segment.is_active ? 'active' : 'inactive'}`}>
                                    {segment.is_active ? 'Active' : 'Inactive'}
                                  </span>
                                  {segment.is_free_public && (
                                    <span className="segment-status active" style={{ marginLeft: '0.35rem' }}>Free</span>
                                  )}
                                </td>
                                <td>{segment.audio_count ?? 0}</td>
                                <td>
                                  <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                                    <button
                                      type="button"
                                      className="action-btn edit-btn dialogue-admin-manage-btn"
                                      onClick={() => void toggleManageSegment(segment.id)}
                                    >
                                      {expandedSegmentId === segment.id ? 'Close' : 'Manage'}
                                    </button>
                                    <button
                                      className="action-btn edit-btn"
                                      onClick={() => handleEditSegment(segment)}
                                      title="Edit metadata"
                                    >
                                      {React.createElement(FiEdit2 as React.ComponentType<{ size?: number }>, { size: 16 })}
                                    </button>
                                    <button
                                      className="action-btn delete-btn"
                                      onClick={() => handleDeleteSegment(segment.id)}
                                      title="Delete"
                                    >
                                      {React.createElement(FiTrash2 as React.ComponentType<{ size?: number }>, { size: 16 })}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {expandedSegmentId != null && (
                        <div className="dialogue-admin-expanded">
                          <div className="dialogue-admin-expanded-header">
                            <h4>{expandedSegmentDetail?.name || 'Segment details'}</h4>
                            {loadingSegmentDetail &&
                              React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, {
                                className: 'spinner',
                                size: 18,
                              })}
                          </div>
                          {expandedSegmentDetail && !loadingSegmentDetail && renderSegmentAudiosPanel(expandedSegmentDetail)}
                        </div>
                      )}

                      <MaterialPagination
                        page={segmentPage}
                        totalPages={segmentTotalPages}
                        totalCount={segmentTotalCount}
                        onPageChange={setSegmentPage}
                        disabled={loadingSegments}
                      />
                    </>
                  )}
                </>
            )}
            
            {/* Podcasts Tab Content */}
            {activeTab === 'podcasts' && (
              <>
                {loadingPodcasts ? (
                  <div className="loading-container">
                    {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, { className: 'spinner', size: 32 })}
                    <p>Loading podcasts...</p>
          </div>
                ) : podcasts.length === 0 ? (
                  <div className="empty-state">
                    {React.createElement(FiPlay as React.ComponentType<{ size?: number }>, { size: 48 })}
                    <p>No podcasts found</p>
                    <p className="empty-state-hint">Click "Add Podcast" to upload your first video</p>
                  </div>
                ) : (
                  <div className="podcasts-grid">
                    {podcasts.map((podcast) => (
                      <motion.div
                        key={podcast.id}
                        className="podcast-card"
                        variants={itemVariants}
                        whileHover={{ y: -5, boxShadow: '0 8px 16px rgba(0,0,0,0.3)' }}
                      >
                        <div className="podcast-thumbnail">
                          <PodcastVideoThumbnail podcastId={podcast.id} alt={podcast.name} />
                          <div className="podcast-overlay">
                            <button
                              className="podcast-play-btn"
                              onClick={() => {
                                // Open admin podcast player
                                setSelectedPodcast(podcast);
                                setIsPlayingPodcast(false);
                                setPodcastCurrentTime(0);
                                setPodcastDuration(0);
                              }}
                            >
                              {React.createElement(FiPlay as React.ComponentType<{ size?: number }>, { size: 24 })}
                            </button>
                          </div>
                        </div>
                        <div className="podcast-info">
                          <h3 className="podcast-name">{podcast.name}</h3>
                          {podcast.description && (
                            <p className="podcast-description">{podcast.description}</p>
                          )}
                          <div className="podcast-meta">
                            <span className="podcast-views">{podcast.views_count} views</span>
                            <span className="podcast-size">{podcast.video_file_size_mb} MB</span>
                            {podcast.duration && (
                              <span className="podcast-duration">{formatDuration(podcast.duration)}</span>
                            )}
                            {podcast.is_free_public && (
                              <span className="segment-status active" title="Free for signed-in users">
                                Free
                              </span>
                            )}
                          </div>
                          <label
                            className="podcast-free-toggle"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.45rem',
                              marginTop: '0.65rem',
                              fontSize: '0.82rem',
                              color: '#ccc',
                              cursor: podcastFreeToggleId === podcast.id ? 'wait' : 'pointer',
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={Boolean(podcast.is_free_public)}
                              disabled={podcastFreeToggleId === podcast.id}
                              onChange={() => {
                                void handleTogglePodcastFreePublic(podcast);
                              }}
                            />
                            Free for signed-in users
                            {podcastFreeToggleId === podcast.id &&
                              React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, {
                                className: 'spinner',
                                size: 14,
                              })}
                          </label>
                        </div>
                        <div className="podcast-actions">
                          <button
                            className="action-btn edit-btn"
                            onClick={() => openEditPodcast(podcast)}
                            title="Edit"
                          >
                            {React.createElement(FiEdit2 as React.ComponentType<{ size?: number }>, { size: 16 })}
                          </button>
                          <button
                            className="action-btn delete-btn"
                            onClick={() => handleDeletePodcast(podcast.id)}
                            title="Delete"
                          >
                            {React.createElement(FiTrash2 as React.ComponentType<{ size?: number }>, { size: 16 })}
                          </button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
                <MaterialPagination
                  page={podcastPage}
                  totalPages={podcastTotalPages}
                  totalCount={podcastTotalCount}
                  onPageChange={setPodcastPage}
                  disabled={loadingPodcasts}
                />
              </>
            )}

            {/* Dialogue Categories Tab Content */}
            {activeTab === 'dialogues' && (
                <>
                  {/* Filters for dialogues */}
                  <motion.div
                    className="material-filters"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    <div className="filter-group">
                      <div className="filter-item category-filter-item">
                        <label>Category</label>
                        <div className="category-filter-controls">
                          <select
                            className="filter-select"
                            value={categoryFilter || ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              setDialoguePage(1);
                              setExpandedDialogueId(null);
                              setExpandedDialogueDetail(null);
                              if (!v) setCategoryFilter(null);
                              else {
                                const n = Number.parseInt(v, 10);
                                setCategoryFilter(Number.isNaN(n) ? null : n);
                              }
                            }}
                          >
                            {dialogueCategories.map((category) => (
                              <option key={category.id} value={category.id}>
                                {category.name}
                              </option>
                            ))}
                          </select>
                          <div className="dialogue-library-total" aria-label="Total dialogues in library">
                            <span className="dialogue-library-total-value">{libraryDialogueTotal}</span>
                            <span className="dialogue-library-total-label">Total Dialogues</span>
                          </div>
                        </div>
                      </div>
                      <div className="search-box">
                        {React.createElement(FiSearch as React.ComponentType<{ size?: number }>, { size: 18 })}
                        <input
                          type="text"
                          placeholder="Search dialogues by name or description..."
                          value={topicSearchTerm}
                          onChange={(e) => {
                            setDialoguePage(1);
                            setTopicSearchTerm(e.target.value);
                          }}
                        />
                      </div>
                    </div>
                  </motion.div>

                  {/* Categories Section */}
                  <motion.div
                    className="categories-section"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    <div className="categories-section-header">
                      <h3 className="section-title section-title-inline">Categories</h3>
                    </div>
                    {loadingCategories ? (
                      <div className="loading-container">
                        {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, { className: 'spinner', size: 24 })}
                        <p>Loading categories...</p>
                      </div>
                    ) : dialogueCategories.length === 0 ? (
                      <div className="empty-state-small">
                        <p>No categories found. Create your first category.</p>
                      </div>
                    ) : (
                      <div className="categories-grid">
                        <input
                          ref={dialogueBriefingFileInputRef}
                          type="file"
                          accept="audio/*"
                          style={{ display: 'none' }}
                          onChange={handleDialogueBriefingFileSelected}
                        />
                        {dialogueCategories.map((category) => (
                          <motion.div
                            key={category.id}
                            className="category-card"
                            variants={itemVariants}
                            whileHover={{ y: -3, boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}
                          >
                            <div className="category-card-header">
                              <h4 className="category-name">{category.name}</h4>
                            </div>
                            {category.description && (
                              <p className="category-description">{category.description}</p>
                            )}
                            <div className="category-meta">
                              <span className={`category-status ${category.is_active ? 'active' : 'inactive'}`}>
                                {category.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                            <div className="category-actions">
                              <button
                                className="action-btn edit-btn"
                                onClick={() => handleEditCategory(category)}
                                title="Edit"
                              >
                                {React.createElement(FiEdit2 as React.ComponentType<{ size?: number }>, { size: 16 })}
                              </button>
                              <button
                                className="action-btn delete-btn"
                                onClick={() => handleDeleteCategory(category.id)}
                                title="Delete"
                              >
                                {React.createElement(FiTrash2 as React.ComponentType<{ size?: number }>, { size: 16 })}
                              </button>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </motion.div>

                  {/* Dialogues section */}
                  <motion.div
                    className="dialogues-section"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    <h3 className="section-title">Dialogues</h3>
                    <p className="dialogue-admin-hint">
                      Dialogues load by category (20 per page). Click Manage to edit parts and voices.
                    </p>
                    {loadingTopics && categoryDialogues.length === 0 ? (
                      <div className="loading-container">
                        {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, { className: 'spinner', size: 24 })}
                        <p>Loading dialogues...</p>
                      </div>
                    ) : !categoryFilter && dialogueCategories.length === 0 ? (
                      <div className="empty-state-small">
                        <p>Create a category first, then add dialogues.</p>
                      </div>
                    ) : categoryDialogues.length === 0 ? (
                      <div className="empty-state-small">
                        <p>{topicSearchTerm.trim() ? 'No dialogues match your search.' : 'No dialogues in this category yet.'}</p>
                      </div>
                    ) : (
                      <>
                        <div className="dialogue-admin-table-wrap" style={{ opacity: loadingTopics ? 0.55 : 1 }}>
                          <table className="dialogue-admin-table">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Status</th>
                                <th>Audios</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {categoryDialogues.map((cd: CategoryDialogue) => (
                                <tr
                                  key={cd.id}
                                  className={expandedDialogueId === cd.id ? 'dialogue-row-active' : undefined}
                                >
                                  <td>
                                    <strong>{cd.name}</strong>
                                    {cd.description && (
                                      <div style={{ color: '#888', fontSize: '0.8rem', marginTop: '0.2rem' }}>
                                        {cd.description.length > 80 ? `${cd.description.slice(0, 80)}…` : cd.description}
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    <span className={`segment-status ${cd.is_active ? 'active' : 'inactive'}`}>
                                      {cd.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                    {cd.is_free_public && (
                                      <span className="segment-status active" style={{ marginLeft: '0.35rem' }}>
                                        Free
                                      </span>
                                    )}
                                  </td>
                                  <td>{cd.audio_count ?? 0}</td>
                                  <td>
                                    <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                                      <button
                                        type="button"
                                        className="action-btn edit-btn dialogue-admin-manage-btn"
                                        onClick={() => void toggleManageDialogue(cd.id)}
                                        title={expandedDialogueId === cd.id ? 'Close' : 'Manage parts & voices'}
                                      >
                                        {expandedDialogueId === cd.id ? 'Close' : 'Manage'}
                                      </button>
                                      <button
                                        className="action-btn edit-btn"
                                        onClick={() => handleEditTopic(cd)}
                                        title="Edit metadata"
                                      >
                                        {React.createElement(FiEdit2 as React.ComponentType<{ size?: number }>, { size: 16 })}
                                      </button>
                                      <button
                                        className="action-btn delete-btn"
                                        onClick={() => handleDeleteTopic(cd.id)}
                                        title="Delete"
                                      >
                                        {React.createElement(FiTrash2 as React.ComponentType<{ size?: number }>, { size: 16 })}
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {expandedDialogueId != null && (
                          <div className="dialogue-admin-expanded">
                            <div className="dialogue-admin-expanded-header">
                              <h4>{expandedDialogueDetail?.name || 'Dialogue details'}</h4>
                              {loadingDialogueDetail && (
                                React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, {
                                  className: 'spinner',
                                  size: 18,
                                })
                              )}
                            </div>
                            {expandedDialogueDetail && !loadingDialogueDetail && renderDialoguePartsPanel(expandedDialogueDetail)}
                          </div>
                        )}

                        <MaterialPagination
                          page={dialoguePage}
                          totalPages={dialogueTotalPages}
                          totalCount={dialogueTotalCount}
                          onPageChange={setDialoguePage}
                          disabled={loadingTopics}
                        />
                      </>
                    )}
                  </motion.div>
                </>
            )}
          </div>
        </div>
      </div>

      {/* Create Podcast Modal */}
      <AnimatePresence>
        {showPodcastModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !uploadingPodcast && (setShowPodcastModal(false), resetPodcastForm())}
          >
            <motion.div
              className="modal-content"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: '600px' }}
            >
              <div className="modal-header">
                <h2>{editingPodcast ? 'Edit Podcast' : 'Add New Podcast'}</h2>
                <button
                  className="modal-close"
                  onClick={() => !uploadingPodcast && (setShowPodcastModal(false), resetPodcastForm())}
                  disabled={uploadingPodcast}
                >
                  {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 24 })}
                </button>
              </div>

              <div className="modal-body">
                <div className="upload-form-group">
                  <label>Podcast Name *</label>
                  <input
                    type="text"
                    value={podcastForm.name}
                    onChange={(e) => setPodcastForm({ ...podcastForm, name: e.target.value })}
                    placeholder="Enter podcast name..."
                    disabled={uploadingPodcast}
                  />
                </div>

                <div className="upload-form-group">
                  <label>Description (Optional)</label>
                  <textarea
                    value={podcastForm.description}
                    onChange={(e) => setPodcastForm({ ...podcastForm, description: e.target.value })}
                    placeholder="Enter description..."
                    rows={3}
                    disabled={uploadingPodcast}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: '#0f0f0f',
                      border: '1px solid #333',
                      borderRadius: '6px',
                      color: '#ffffff',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div className="upload-form-group">
                  <label>Video File {editingPodcast ? '(Optional — leave empty to keep current)' : '*'}</label>
                  <input
                    id="podcast-video-input"
                    type="file"
                    accept="video/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (!file.type.startsWith('video/')) {
                          setError('Please select a video file');
                          return;
                        }
                        if (file.size > 500 * 1024 * 1024) {
                          setError('Video file size must be less than 500MB');
                          return;
                        }
                        setPodcastForm({ ...podcastForm, video_file: file });
                        setError(null);
                      }
                    }}
                    disabled={uploadingPodcast}
                  />
                  {podcastForm.video_file && (
                    <div className="file-info">
                      {React.createElement(FiPlay as React.ComponentType<{ size?: number }>, { size: 20 })}
                      <div>
                        <div className="file-name">{podcastForm.video_file.name}</div>
                        <div className="file-size">{formatFileSize(podcastForm.video_file.size)}</div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="upload-form-group">
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: uploadingPodcast ? 'not-allowed' : 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={podcastForm.is_free_public}
                      onChange={(e) => setPodcastForm({ ...podcastForm, is_free_public: e.target.checked })}
                      disabled={uploadingPodcast}
                      style={{ marginTop: '0.2rem' }}
                    />
                    <span>
                      <strong>Free access</strong>
                      <span style={{ display: 'block', fontSize: '0.8rem', color: '#aaa', fontWeight: 400, marginTop: '0.25rem' }}>
                        Allow all signed-in users to watch without a subscription (public catalogue).
                      </span>
                    </span>
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn-secondary"
                  onClick={() => {
                    setShowPodcastModal(false);
                    resetPodcastForm();
                  }}
                  disabled={uploadingPodcast}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={handleCreatePodcast}
                  disabled={
                    !podcastForm.name.trim() ||
                    uploadingPodcast ||
                    (!editingPodcast && !podcastForm.video_file)
                  }
                >
                  {uploadingPodcast ? (
                    <>
                      {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, { className: 'spinner', size: 18 })}
                      {editingPodcast ? 'Saving...' : 'Uploading...'}
                    </>
                  ) : (
                    editingPodcast ? 'Save changes' : 'Create Podcast'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upload Modal */}
      <AnimatePresence>
        {showUploadModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !uploading && setShowUploadModal(false)}
          >
            <motion.div
              className="modal-content"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>Upload Material</h2>
                <button
                  className="modal-close"
                  onClick={() => !uploading && (setShowUploadModal(false), resetUploadForm())}
                  disabled={uploading}
                >
                  {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 24 })}
                </button>
              </div>

              <div className="modal-body">
                <div className="upload-form-group">
                  <label>Audio File *</label>
                  <input
                    id="audio-file-input"
                    type="file"
                    accept="audio/*"
                    onChange={handleFileSelect}
                    disabled={uploading}
                  />
                  {selectedFile && (
                    <div className="file-info">
                      {React.createElement(FiMusic as React.ComponentType<{ size?: number }>, { size: 20 })}
                      <span>{selectedFile?.name}</span>
                      <span className="file-size">({formatFileSize(selectedFile?.size || 0)})</span>
                    </div>
                  )}
                </div>

                <div className="upload-form-group">
                  <label>Segment Number (Optional)</label>
                  <input
                    type="number"
                    value={segmentNumber}
                    onChange={(e) => setSegmentNumber(e.target.value)}
                    placeholder="e.g., 1, 2, 3..."
                    disabled={uploading}
                  />
                </div>

                <div className="upload-form-group">
                  <label>Test Category (Optional)</label>
                  <input
                    type="text"
                    value={testCategory}
                    onChange={(e) => setTestCategory(e.target.value)}
                    placeholder="e.g., Housing, Financial, Education..."
                    disabled={uploading}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn-secondary"
                  onClick={() => {
                    setShowUploadModal(false);
                    resetUploadForm();
                  }}
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={handleUpload}
                  disabled={!selectedFile || uploading}
                >
                  {uploading ? (
                    <>
                      {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, { className: 'spinner', size: 18 })}
                      Uploading...
                    </>
                  ) : (
                    <>
                      {React.createElement(FiUpload as React.ComponentType<{ size?: number }>, { size: 18 })}
                      Upload
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Segment Modal */}
      <AnimatePresence>
        {showSegmentModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !loadingSegments && (setShowSegmentModal(false), resetSegmentForm())}
          >
            <motion.div
              className="modal-content"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>{editingSegment ? 'Edit Segment' : 'Create Segment'}</h2>
                <button
                  className="modal-close"
                  onClick={() => {
                    setShowSegmentModal(false);
                    resetSegmentForm();
                  }}
                  disabled={loadingSegments}
                >
                  {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 24 })}
                </button>
              </div>

              <div className="modal-body">
                <div className="upload-form-group">
                  <label>Segment Name *</label>
                  <input
                    type="text"
                    value={segmentForm.name}
                    onChange={(e) => setSegmentForm({ ...segmentForm, name: e.target.value })}
                    placeholder="e.g., Lesson 1, Dialogue 1..."
                    disabled={loadingSegments}
                  />
                </div>

                <div className="upload-form-group">
                  <label>Exercise notes (shown after answers)</label>
                  <textarea
                    className="bilingual-text--textarea mm-exercise-notes-input"
                    value={segmentForm.description}
                    onChange={(e) => setSegmentForm({ ...segmentForm, description: e.target.value })}
                    placeholder={`1. Title / عنوان\n\nاز متن: …\nمثال مشابه: …\n🔹 کاربرد: …\n\n2. …`}
                    rows={8}
                    disabled={loadingSegments}
                    dir="auto"
                  />
                </div>

                <div className="upload-form-group">
                  <label>Segment Number (Optional)</label>
                  <input
                    type="number"
                    value={segmentForm.segment_number}
                    onChange={(e) => setSegmentForm({ ...segmentForm, segment_number: e.target.value })}
                    placeholder="e.g., 1, 2, 3..."
                    disabled={loadingSegments}
                  />
                </div>

                <div className="upload-form-group">
                  <label>Test Category (Optional)</label>
                  <input
                    type="text"
                    value={segmentForm.test_category}
                    onChange={(e) => setSegmentForm({ ...segmentForm, test_category: e.target.value })}
                    placeholder="e.g., Housing, Financial, Education..."
                    disabled={loadingSegments}
                  />
                </div>

                <div className="upload-form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={segmentForm.is_active}
                      onChange={(e) => setSegmentForm({ ...segmentForm, is_active: e.target.checked })}
                      disabled={loadingSegments}
                    />
                    Active
                  </label>
                </div>

                <div className="upload-form-group">
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: loadingSegments ? 'not-allowed' : 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={segmentForm.is_free_public}
                      onChange={(e) => setSegmentForm({ ...segmentForm, is_free_public: e.target.checked })}
                      disabled={loadingSegments}
                      style={{ marginTop: '0.2rem' }}
                    />
                    <span>
                      <strong>Free access</strong>
                      <span style={{ display: 'block', fontSize: '0.8rem', color: '#aaa', fontWeight: 400, marginTop: '0.25rem' }}>
                        Materials in this segment available to all signed-in users without subscription.
                      </span>
                    </span>
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn-secondary"
                  onClick={() => {
                    setShowSegmentModal(false);
                    resetSegmentForm();
                  }}
                  disabled={loadingSegments}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={() => editingSegment ? handleUpdateSegment(editingSegment.id) : handleCreateSegment()}
                  disabled={!segmentForm.name.trim() || loadingSegments}
                >
                  {loadingSegments ? (
                    <>
                      {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, { className: 'spinner', size: 18 })}
                      {editingSegment ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    editingSegment ? 'Update' : 'Create'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pick audio from library for a structural segment slot */}
      <AnimatePresence>
        {showAddAudioModal && selectedSegmentForAudio != null && segmentLibrarySlotOrder != null && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => closeSegmentAudioModal()}
          >
            <motion.div
              className="modal-content mm-segment-library-modal"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>
                  Choose audio for slot {segmentLibrarySlotOrder + 1} —{' '}
                  {STRUCTURAL_SEGMENT_AUDIO_SLOTS[segmentLibrarySlotOrder]?.label ?? 'Audio'}
                </h2>
                <button
                  className="modal-close"
                  onClick={() => closeSegmentAudioModal()}
                  type="button"
                >
                  {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 24 })}
                </button>
              </div>

              <div className="modal-body">
                <p className="mm-segment-library-hint">
                  {STRUCTURAL_SEGMENT_AUDIO_SLOTS[segmentLibrarySlotOrder]?.description}
                </p>

                <h3 className="mm-segment-library-section">Lab (TTS)</h3>
                <div className="audio-selection-list mm-segment-library-list">
                  {labItems.filter((m) => m.is_active).length === 0 ? (
                    <p className="mm-segment-library-empty">No lab items yet.</p>
                  ) : (
                    labItems
                      .filter((m) => m.is_active)
                      .map((material) => {
                        const seg =
                          expandedSegmentDetail?.id === selectedSegmentForAudio
                            ? expandedSegmentDetail
                            : segments.find((s: StructuralSegment) => s.id === selectedSegmentForAudio);
                        const isInSegment = seg ? isMaterialUsedInSegment(seg, material.id) : false;
                        return (
                          <div
                            key={`lab-${material.id}`}
                            className={`audio-selection-item ${isInSegment ? 'disabled' : ''}`}
                          >
                            <div className="audio-selection-info">
                              {React.createElement(FiMusic as React.ComponentType<{ size?: number }>, { size: 20 })}
                              <div>
                                <div className="audio-selection-name">{material.file_name}</div>
                                <div className="audio-selection-meta">{formatFileSize(material.file_size)}</div>
                              </div>
                            </div>
                            {isInSegment ? (
                              <span className="already-added">In segment</span>
                            ) : (
                              <button
                                type="button"
                                className="add-existing-btn"
                                onClick={() => void handlePickAudioForSlot(material.id)}
                              >
                                Use this
                              </button>
                            )}
                          </div>
                        );
                      })
                  )}
                </div>

                <h3 className="mm-segment-library-section">Materials library</h3>
                <div className="audio-selection-list mm-segment-library-list">
                  {materials.filter((m) => m.is_active).length === 0 ? (
                    <p className="mm-segment-library-empty">No materials in library.</p>
                  ) : (
                    materials
                      .filter((m) => m.is_active)
                      .map((material) => {
                        const seg =
                          expandedSegmentDetail?.id === selectedSegmentForAudio
                            ? expandedSegmentDetail
                            : segments.find((s: StructuralSegment) => s.id === selectedSegmentForAudio);
                        const isInSegment = seg ? isMaterialUsedInSegment(seg, material.id) : false;
                        return (
                          <div
                            key={`mat-${material.id}`}
                            className={`audio-selection-item ${isInSegment ? 'disabled' : ''}`}
                          >
                            <div className="audio-selection-info">
                              {React.createElement(FiMusic as React.ComponentType<{ size?: number }>, { size: 20 })}
                              <div>
                                <div className="audio-selection-name">{material.file_name}</div>
                                <div className="audio-selection-meta">
                                  {formatFileSize(material.file_size)} • {material.test_category || 'No category'}
                                </div>
                              </div>
                            </div>
                            {isInSegment ? (
                              <span className="already-added">In segment</span>
                            ) : (
                              <button
                                type="button"
                                className="add-existing-btn"
                                onClick={() => void handlePickAudioForSlot(material.id)}
                              >
                                Use this
                              </button>
                            )}
                          </div>
                        );
                      })
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Admin Podcast Player Modal */}
      <AnimatePresence>
        {selectedPodcast && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              setSelectedPodcast(null);
              setIsPlayingPodcast(false);
              const video = adminVideoRef.current;
              if (video) {
                video.pause();
                video.src = '';
              }
            }}
            style={{ zIndex: 2000 }}
          >
            <motion.div
              className="admin-podcast-player-modal"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: '1200px', width: '90%', background: '#1a1a1a', borderRadius: '12px', padding: '1.5rem' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h2 style={{ color: '#ffffff', margin: 0 }}>{selectedPodcast.name}</h2>
                <button
                  onClick={() => {
                    setSelectedPodcast(null);
                    setIsPlayingPodcast(false);
                    const video = adminVideoRef.current;
                    if (video) {
                      video.pause();
                      video.src = '';
                    }
                  }}
                  style={{ background: 'transparent', border: 'none', color: '#ffffff', cursor: 'pointer', padding: '0.5rem' }}
                >
                  {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 24 })}
                </button>
              </div>
              
              <div className="video-wrapper" style={{ marginBottom: '1rem' }}>
                <div className="video-container">
                  <video
                    ref={adminVideoRef}
                    className="video-player"
                    playsInline
                    onMouseMove={() => {
                      setShowPodcastControls(true);
                      if (podcastControlsTimeoutRef.current) {
                        clearTimeout(podcastControlsTimeoutRef.current);
                      }
                      podcastControlsTimeoutRef.current = setTimeout(() => {
                        setShowPodcastControls(false);
                      }, 3000);
                    }}
                    onClick={handlePlayPausePodcast}
                  />
                  
                  {podcastBuffering && (
                    <div className="buffering-indicator">
                      {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, { 
                        className: 'spinner', 
                        size: 32 
                      })}
                    </div>
                  )}

                  <motion.div
                    className={`video-controls ${showPodcastControls ? 'visible' : 'hidden'}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: showPodcastControls ? 1 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* Progress Bar */}
                    <div className="progress-bar-container" onClick={handleSeekPodcast}>
                      <div className="progress-bar">
                        <div
                          className="progress-filled"
                          style={{ width: `${podcastDuration > 0 ? (podcastCurrentTime / podcastDuration) * 100 : 0}%` }}
                        />
                        <div
                          className="progress-handle"
                          style={{ left: `${podcastDuration > 0 ? (podcastCurrentTime / podcastDuration) * 100 : 0}%` }}
                        />
                      </div>
                    </div>

                    {/* Controls */}
                    <div className="controls-bar">
                      <div className="controls-left">
                        {/* Play/Pause Button */}
                        <button
                          className="control-btn play-pause-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePlayPausePodcast();
                          }}
                          aria-label={isPlayingPodcast ? 'Pause' : 'Play'}
                        >
                          {isPlayingPodcast
                            ? React.createElement(FiPause as React.ComponentType<{ size?: number }>, { size: 22 })
                            : React.createElement(FiPlay as React.ComponentType<{ size?: number }>, { size: 22 })
                          }
                        </button>

                        {/* Time Display */}
                        <div className="time-display">
                          <span>{formatTime(podcastCurrentTime)}</span>
                          <span>/</span>
                          <span>{formatTime(podcastDuration)}</span>
                        </div>

                        {/* Volume Control */}
                        <div 
                          className="volume-control-wrapper"
                          ref={podcastVolumeControlRef}
                          onMouseEnter={() => setShowPodcastVolumeSlider(true)}
                          onMouseLeave={() => setShowPodcastVolumeSlider(false)}
                        >
                          <button
                            className="control-btn volume-btn-circle"
                            onClick={(e) => {
                              e.stopPropagation();
                              togglePodcastMute();
                            }}
                            aria-label={isPodcastMuted ? 'Unmute' : 'Mute'}
                          >
                            {isPodcastMuted
                              ? React.createElement(FiVolumeX as React.ComponentType<{ size?: number }>, { size: 22 })
                              : React.createElement(FiVolume2 as React.ComponentType<{ size?: number }>, { size: 22 })
                            }
                          </button>
                          
                          {showPodcastVolumeSlider && (
                            <div className="volume-slider-popup">
                              <div className="volume-slider-container">
                                <div className="volume-slider-wrapper">
                                  <input
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.01"
                                    value={isPodcastMuted ? 0 : podcastVolume}
                                    onChange={handlePodcastVolumeChange}
                                    onClick={(e) => e.stopPropagation()}
                                    className="volume-slider-horizontal"
                                    aria-label="Volume"
                                  />
                                </div>
                                <div className="volume-value-display">
                                  {Math.round((isPodcastMuted ? 0 : podcastVolume) * 100)}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="controls-right">
                        {/* Fullscreen Button */}
                        <button
                          className="control-btn fullscreen-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            togglePodcastFullscreen();
                          }}
                          aria-label="Fullscreen"
                        >
                          {React.createElement(FiMaximize as React.ComponentType<{ size?: number }>, { size: 18 })}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>

              {selectedPodcast.description && (
                <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginTop: '1rem' }}>
                  {selectedPodcast.description}
                </p>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Category Modal */}
      <AnimatePresence>
        {showCategoryModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !loadingCategories && (setShowCategoryModal(false), resetCategoryForm())}
          >
            <motion.div
              className="modal-content"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>{editingCategory ? 'Edit Category' : 'Create Category'}</h2>
                <button
                  className="modal-close"
                  onClick={() => {
                    setShowCategoryModal(false);
                    resetCategoryForm();
                  }}
                  disabled={loadingCategories}
                >
                  {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 24 })}
                </button>
              </div>

              <div className="modal-body">
                <div className="upload-form-group">
                  <label>Category Name *</label>
                  <input
                    type="text"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                    placeholder="e.g., Housing, Financial, Education..."
                    disabled={loadingCategories}
                  />
                </div>

                <div className="upload-form-group">
                  <label>Description (Optional)</label>
                  <textarea
                    value={categoryForm.description}
                    onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                    placeholder="Enter description..."
                    rows={3}
                    disabled={loadingCategories}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: '#0f0f0f',
                      border: '1px solid #333',
                      borderRadius: '6px',
                      color: '#ffffff',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div className="upload-form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={categoryForm.is_active}
                      onChange={(e) => setCategoryForm({ ...categoryForm, is_active: e.target.checked })}
                      disabled={loadingCategories}
                    />
                    Active
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn-secondary"
                  onClick={() => {
                    setShowCategoryModal(false);
                    resetCategoryForm();
                  }}
                  disabled={loadingCategories}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={() => editingCategory ? handleUpdateCategory(editingCategory.id) : handleCreateCategory()}
                  disabled={!categoryForm.name.trim() || loadingCategories}
                >
                  {loadingCategories ? (
                    <>
                      {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, { className: 'spinner', size: 18 })}
                      {editingCategory ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    editingCategory ? 'Update' : 'Create'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Category dialogue modal */}
      <AnimatePresence>
        {showTopicModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !loadingTopics && (setShowTopicModal(false), resetTopicForm())}
          >
            <motion.div
              className="modal-content"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>{editingTopic ? 'Edit dialogue' : 'Create dialogue'}</h2>
                <button
                  className="modal-close"
                  onClick={() => {
                    setShowTopicModal(false);
                    resetTopicForm();
                  }}
                  disabled={loadingTopics}
                >
                  {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 24 })}
                </button>
              </div>

              <div className="modal-body">
                <div className="upload-form-group">
                  <label>Category *</label>
                  <select
                    value={topicForm.category}
                    onChange={(e) => setTopicForm({ ...topicForm, category: e.target.value })}
                    disabled={loadingTopics || !!editingTopic}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: '#0f0f0f',
                      border: '1px solid #333',
                      borderRadius: '6px',
                      color: '#ffffff',
                      fontSize: '0.95rem'
                    }}
                  >
                    <option value="">Select a category</option>
                    {dialogueCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="upload-form-group">
                  <label>Dialogue name *</label>
                  <input
                    type="text"
                    value={topicForm.name}
                    onChange={(e) => setTopicForm({ ...topicForm, name: e.target.value })}
                    placeholder="e.g., Lesson 1, Dialogue 1..."
                    disabled={loadingTopics}
                  />
                </div>

                <div className="upload-form-group">
                  <label>Description (Optional)</label>
                  <textarea
                    value={topicForm.description}
                    onChange={(e) => setTopicForm({ ...topicForm, description: e.target.value })}
                    placeholder="Enter description..."
                    rows={3}
                    disabled={loadingTopics}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: '#0f0f0f',
                      border: '1px solid #333',
                      borderRadius: '6px',
                      color: '#ffffff',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div className="upload-form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={topicForm.is_active}
                      onChange={(e) => setTopicForm({ ...topicForm, is_active: e.target.checked })}
                      disabled={loadingTopics}
                    />
                    Active
                  </label>
                </div>

                <div className="upload-form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={topicForm.is_free_public}
                      onChange={(e) => setTopicForm({ ...topicForm, is_free_public: e.target.checked })}
                      disabled={loadingTopics}
                    />
                    Free for everyone (no subscription)
                  </label>
                </div>

                <div className="upload-form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={topicForm.enable_as_mock}
                      onChange={(e) => setTopicForm({ ...topicForm, enable_as_mock: e.target.checked })}
                      disabled={loadingTopics}
                    />
                    Enable as Mock service too
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn-secondary"
                  onClick={() => {
                    setShowTopicModal(false);
                    resetTopicForm();
                  }}
                  disabled={loadingTopics}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={() => editingTopic ? handleUpdateTopic(editingTopic.id) : handleCreateTopic()}
                  disabled={!topicForm.name.trim() || !topicForm.category || loadingTopics}
                >
                  {loadingTopics ? (
                    <>
                      {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, { className: 'spinner', size: 18 })}
                      {editingTopic ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    editingTopic ? 'Update' : 'Create'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add audio to part modal */}
      <AnimatePresence>
        {showAddAudioToTopicModal && selectedTopicForAudio && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() =>
              !uploadingAudios &&
              (setShowAddAudioToTopicModal(false),
              setSelectedTopicForAudio(null),
              setSelectedDialogueForAudio(null),
              setSelectedAudioFiles([]))
            }
          >
            <motion.div
              className="modal-content"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}
            >
              <div className="modal-header">
                <h2>Add audio to dialogue</h2>
                <button
                  className="modal-close"
                  onClick={() => {
                    setShowAddAudioToTopicModal(false);
                    setSelectedTopicForAudio(null);
                    setSelectedDialogueForAudio(null);
                    setSelectedAudioFiles([]);
                    const fileInput = document.getElementById('topic-audio-file-input') as HTMLInputElement;
                    if (fileInput) fileInput.value = '';
                  }}
                  disabled={uploadingAudios}
                >
                  {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 24 })}
                </button>
              </div>

              <div className="modal-body">
                {selectedTopicForAudio != null && (
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', color: '#aaa', fontSize: '0.85rem', marginBottom: '0.35rem' }}>
                      Part
                    </label>
                    <select
                      className="filter-select"
                      style={{ width: '100%', maxWidth: '100%' }}
                      value={selectedDialogueForAudio ?? ''}
                      onChange={(e) => setSelectedDialogueForAudio(Number(e.target.value) || null)}
                    >
                      <option value="">Select part…</option>
                      {categoryDialogues
                        .filter((t) => t.id === selectedTopicForAudio)
                        .flatMap((t) => [...(t.parts || [])].sort((a, b) => a.order - b.order))
                        .map((d: DialoguePart) => (
                          <option key={d.id} value={d.id}>
                            {(d.name || '').trim() || `Part ${d.order + 1}`}
                          </option>
                        ))}
                    </select>
                  </div>
                )}
                {/* Upload New Audio Files */}
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ color: '#ffffff', marginBottom: '1rem', fontSize: '1.1rem' }}>Upload New Audio Files</h3>
                  <div className="upload-form-group">
                    <label>Select Audio File(s) *</label>
                    <input
                      id="topic-audio-file-input"
                      type="file"
                      accept="audio/*"
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        setSelectedAudioFiles(files);
                      }}
                      disabled={uploadingAudios}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: '#0f0f0f',
                        border: '1px dashed #333',
                        borderRadius: '6px',
                        color: '#ffffff',
                        cursor: 'pointer'
                      }}
                    />
                    <p style={{ color: '#888', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                      You can select multiple files at once
                    </p>
                  </div>

                  {selectedAudioFiles.length > 0 && (
                    <div className="selected-files-list">
                      <h4 style={{ color: '#ffffff', marginBottom: '0.75rem', fontSize: '0.95rem' }}>
                        Selected Files ({selectedAudioFiles.length})
                      </h4>
                      {selectedAudioFiles.map((file, index) => (
                        <div key={index} className="selected-file-item">
                          <div className="selected-file-info">
                            {React.createElement(FiMusic as React.ComponentType<{ size?: number }>, { size: 18 })}
                            <div>
                              <div className="selected-file-name">{file.name}</div>
                              <div className="selected-file-size">{formatFileSize(file.size)}</div>
                            </div>
                          </div>
                          <button
                            className="remove-file-btn"
                            onClick={() => {
                              const newFiles = [...selectedAudioFiles];
                              newFiles.splice(index, 1);
                              setSelectedAudioFiles(newFiles);
                            }}
                            disabled={uploadingAudios}
                          >
                            {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 16 })}
                          </button>
                        </div>
                      ))}
                      <button
                        className="btn-primary"
                        onClick={async () => {
                          if (!selectedTopicForAudio || selectedDialogueForAudio == null || selectedAudioFiles.length === 0) {
                            setError('Select a part and at least one file.');
                            return;
                          }
                          setUploadingAudios(true);
                          try {
                            for (const file of selectedAudioFiles) {
                              // Upload file first
                              const uploadedAudio = await materialAPI.uploadMaterial({
                                audio_file: file
                              });
                              // Then add to dialogue
                              await adminDialogueCategoriesAPI.addAudioToDialoguePart(selectedTopicForAudio, {
                                part_id: selectedDialogueForAudio,
                                audio_file_id: uploadedAudio.id
                              });
                            }
                            setSuccess(`Successfully uploaded ${selectedAudioFiles.length} file(s) to dialogue`);
                            setShowAddAudioToTopicModal(false);
                            setSelectedTopicForAudio(null);
                            setSelectedDialogueForAudio(null);
                            setSelectedAudioFiles([]);
                            const fileInput = document.getElementById('topic-audio-file-input') as HTMLInputElement;
                            if (fileInput) fileInput.value = '';
                            fetchDialogueCategories();
      refreshDialogueViews();
                            fetchMaterials();
                          } catch (err: any) {
                            setError(err.message || 'Failed to upload audios to dialogue');
                          } finally {
                            setUploadingAudios(false);
                          }
                        }}
                        disabled={uploadingAudios || selectedAudioFiles.length === 0}
                        style={{ width: '100%', marginTop: '1rem' }}
                      >
                        {uploadingAudios ? (
                          <>
                            {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, { className: 'spinner', size: 18 })}
                            Uploading...
                          </>
                        ) : (
                          <>
                            {React.createElement(FiUpload as React.ComponentType<{ size?: number }>, { size: 18 })}
                            Upload {selectedAudioFiles.length} file(s) to dialogue
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>

                {/* Divider */}
                <div style={{ 
                  height: '1px', 
                  background: '#333', 
                  margin: '2rem 0',
                  position: 'relative'
                }}>
                  <div style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    background: '#1a1a1a',
                    padding: '0 1rem',
                    color: '#888',
                    fontSize: '0.85rem'
                  }}>
                    OR
                  </div>
                </div>

                {/* Select from Lab */}
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ color: '#ffffff', marginBottom: '1rem', fontSize: '1.1rem' }}>Select from Lab</h3>
                  <p style={{ color: '#888', marginBottom: '1rem', fontSize: '0.9rem' }}>
                    Choose from TTS-generated audio in Lab:
                  </p>
                  <div className="materials-list" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {labItems.filter((m: AudioFile) => m.is_active).length === 0 ? (
                      <p style={{ color: '#888', textAlign: 'center', padding: '1.5rem' }}>
                        No lab items. Generate audio in the Lab page first.
                      </p>
                    ) : (
                      labItems
                        .filter((m: AudioFile) => m.is_active)
                        .map((material: AudioFile) => (
                          <div key={material.id} className="material-item">
                            <div className="material-info">
                              {React.createElement(FiMusic as React.ComponentType<{ size?: number }>, { size: 18 })}
                              <div>
                                <div className="material-name">{material.file_name}</div>
                                <div className="material-meta">
                                  {material.file_size_mb} MB
                                </div>
                              </div>
                            </div>
                            <button
                              className="edit-content-btn"
                              onClick={() => setEditingContentAudioId(material.id)}
                              title="Edit source text and translations"
                            >
                              Content
                            </button>
                            <button
                              className="btn-primary-small"
                              onClick={() => handleAddAudioToTopic(material.id)}
                              disabled={uploadingAudios}
                            >
                              Add
                            </button>
                          </div>
                        ))
                    )}
                  </div>
                </div>

                {/* Divider */}
                <div style={{ 
                  height: '1px', 
                  background: '#333', 
                  margin: '2rem 0',
                  position: 'relative'
                }}>
                  <div style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    background: '#1a1a1a',
                    padding: '0 1rem',
                    color: '#888',
                    fontSize: '0.85rem'
                  }}>
                    OR
                  </div>
                </div>

                {/* Select Existing Audio Files */}
                <div>
                  <h3 style={{ color: '#ffffff', marginBottom: '1rem', fontSize: '1.1rem' }}>Select Existing Audio Files</h3>
                  {materials.filter((m: AudioFile) => m.audio_type === 'system' && m.is_active).length === 0 ? (
                    <p style={{ color: '#888', fontSize: '0.9rem' }}>No system audio files available</p>
                  ) : (
                    <div className="materials-list" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {materials
                        .filter((m: AudioFile) => m.audio_type === 'system' && m.is_active)
                        .map((material: AudioFile) => (
                          <div key={material.id} className="material-item">
                            <div className="material-info">
                              {React.createElement(FiMusic as React.ComponentType<{ size?: number }>, { size: 18 })}
                              <div>
                                <div className="material-name">{material.file_name}</div>
                                <div className="material-meta">
                                  {material.file_size_mb} MB
                                  {material.duration && ` • ${formatDuration(material.duration)}`}
                                </div>
                              </div>
                            </div>
                            <button
                              className="edit-content-btn"
                              onClick={() => setEditingContentAudioId(material.id)}
                              title="Edit source text and translations"
                            >
                              Content
                            </button>
                            <button
                              className="btn-primary-small"
                              onClick={() => handleAddAudioToTopic(material.id)}
                              disabled={uploadingAudios}
                            >
                              Add
                            </button>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {editingContentAudioId && (
        <AudioContentEditor
          audioId={editingContentAudioId}
          audioFileName={resolveContentFileName(editingContentAudioId)}
          onClose={() => setEditingContentAudioId(null)}
          onSaved={() => setEditingContentAudioId(null)}
        />
      )}

      {bulkContentPart && (
        <DialogueBulkContentModal
          topicId={bulkContentPart.topicId}
          partId={bulkContentPart.partId}
          partName={bulkContentPart.partName}
          audios={bulkContentPart.audios}
          onClose={() => setBulkContentPart(null)}
          onSaved={() => {
            void refreshDialogueViews();
          }}
        />
      )}
    </div>
  );
};

export default MaterialManagement;

